(() => {
  'use strict';

  // Observation only: no artwork, identifiers, URLs, text or server telemetry.
  const exportDiagnostics = (() => {
    const key = 'dim-export-diagnostics-v1';
    const ttl = 24 * 60 * 60 * 1000;
    const stages = new Set(['start', 'admission', 'text', 'capacity', 'allocate', 'context', 'draw', 'encode', 'null-blob', 'verify-png', 'verify-capacity', 'decode', 'rendered', 'prepared-storage', 'fingerprint', 'draft-storage', 'transition', 'complete']);
    const codes = new Set(['IMAGE_OUTPUT_CAPACITY', 'IMAGE_MEMORY_LIMIT', 'IMAGE_DIMENSIONS_EXCESSIVE', 'IMAGE_BYTES_EXCESSIVE', 'IMAGE_FORMAT_INVALID', 'IMAGE_TRUNCATED', 'PNG_IMAGE_DATA_INVALID', 'PNG_IMAGE_DATA_TRUNCATED']);
    const names = new Set(['Error', 'RangeError', 'QuotaExceededError', 'IndexSizeError', 'SecurityError', 'InvalidStateError', 'AbortError', 'DataCloneError', 'NotAllowedError']);
    const number = (value) => Number.isSafeInteger(value) && value >= 0 && value <= Number.MAX_SAFE_INTEGER ? value : 0;
    const choice = (value, allowed, fallback = 'unknown') => allowed.includes(value) ? value : fallback;
    const clean = (record) => ({
      startedAt: number(record.startedAt), updatedAt: number(record.updatedAt),
      status: choice(record.status, ['running', 'success', 'failure', 'interrupted']),
      stage: stages.has(record.stage) ? record.stage : 'start',
      checkpoints: Array.isArray(record.checkpoints) ? record.checkpoints.slice(-20).filter(item => stages.has(item?.stage)).map(item => ({ stage: item.stage, elapsedMs: number(item.elapsedMs) })) : [],
      code: codes.has(record.code) ? record.code : 'unknown', name: names.has(record.name) ? record.name : 'unknown',
      revision: number(record.revision), codeRevision: 'export-diagnostics@1', themeId: /^\d{1,20}$/.test(String(record.themeId || '')) ? String(record.themeId) : 'unknown',
      browser: choice(record.browser, ['Safari', 'Chrome', 'Firefox', 'Edge']), browserMajor: number(record.browserMajor),
      platform: choice(record.platform, ['iOS', 'Android', 'Windows', 'macOS', 'Linux']),
      sourceWidth: number(record.sourceWidth), sourceHeight: number(record.sourceHeight), sourceBytes: number(record.sourceBytes),
      sourceType: choice(record.sourceType, ['image/png', 'image/jpeg']), provenance: choice(record.provenance, ['upload', 'create']),
      outputWidth: number(record.outputWidth), outputHeight: number(record.outputHeight), outputBytes: number(record.outputBytes),
      textCount: number(record.textCount), textPresent: record.textPresent === true
    });
    let records = [];
    let persistent = true;
    const panels = [];
    try {
      const raw = window.localStorage?.getItem(key);
      if (raw && raw.length <= 20000) {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) records = parsed.slice(-3).filter(record => record && record.startedAt > Date.now() - ttl && record.startedAt <= Date.now()).map(clean);
        records.forEach(record => { if (record.status === 'running') record.status = 'interrupted'; });
      }
    } catch (_) { persistent = false; }
    const prune = () => { records = records.filter(record => record.startedAt > Date.now() - ttl).slice(-3); };
    const report = () => { save(); return JSON.stringify({ format: 'dim-export-diagnostics@1', retentionHours: 24, persistent, attempts: records.map(clean) }, null, 2); };
    const refresh = () => panels.forEach(panel => { panel.hidden = !records.some(record => ['failure', 'interrupted'].includes(record.status)); panel.dataset.diagnosticsPersistence = persistent ? 'local-storage' : 'current-page-only'; if (panel.persistenceNote) panel.persistenceNote.textContent = persistent ? 'Saved on this browser for 24 hours.' : 'Browser storage is unavailable. Copy or download this report before leaving this page.'; });
    const save = () => {
      prune();
      try { if (!window.localStorage) throw new Error(); window.localStorage.setItem(key, JSON.stringify(records.map(clean))); }
      catch (_) { persistent = false; }
      refresh();
    };
    save();
    const attach = (container, after = null) => {
      if (!container?.appendChild || container.querySelector?.('[data-export-diagnostics]')) return;
      const panel = document.createElement('section');
      panel.hidden = true; panel.style.cssText = 'margin-block:1rem;padding:1rem;min-width:0';
      panel.setAttribute('aria-label', 'Image export diagnostics');
      panel.dataset.exportDiagnostics = 'true';
      const explanation = document.createElement('p');
      explanation.textContent = 'Export diagnostics are available for support. An interrupted attempt records the last reached step; it cannot identify why the browser stopped. Reports contain technical details only and expire after 24 hours.';
      const status = document.createElement('p'); status.setAttribute('role', 'status');
      panel.persistenceNote = document.createElement('p');
      const fallback = document.createElement('textarea'); fallback.hidden = true; fallback.readOnly = true;
      fallback.setAttribute('aria-label', 'Export diagnostics to copy');
      fallback.style.cssText = 'width:100%;max-width:100%;min-height:12rem;box-sizing:border-box';
      const copy = document.createElement('button'); copy.type = 'button'; copy.textContent = 'Copy diagnostics'; copy.className = 'button button--secondary';
      copy.addEventListener('click', async () => {
        const value = report();
        try { await window.navigator.clipboard.writeText(value); status.textContent = 'Diagnostics copied.'; }
        catch (_) { fallback.value = value; fallback.hidden = false; fallback.focus(); fallback.select(); fallback.setSelectionRange?.(0, value.length); status.textContent = 'Touch and hold the selected report, then choose Copy, or use Download diagnostics.'; }
      });
      const download = document.createElement('button'); download.type = 'button'; download.textContent = 'Download diagnostics'; download.className = 'button button--secondary';
      download.addEventListener('click', () => {
        try {
          const url = URL.createObjectURL(new Blob([report()], { type: 'application/json' }));
          const link = document.createElement('a'); link.href = url; link.download = 'dream-it-made-export-diagnostics.json';
          panel.appendChild(link); link.click(); link.remove(); window.setTimeout(() => URL.revokeObjectURL(url), 60000);
          status.textContent = 'Diagnostics download opened. If your browser shows the report, use Share to save or send it.';
        } catch (_) { fallback.value = report(); fallback.hidden = false; fallback.focus(); fallback.select(); status.textContent = 'Select and copy the report below.'; }
      });
      const actions = document.createElement('div'); actions.style.cssText = 'display:flex;flex-wrap:wrap;gap:0.75rem';
      actions.appendChild(copy); actions.appendChild(download);
      [explanation, panel.persistenceNote, actions, status, fallback].forEach(element => panel.appendChild(element));
      if (after?.parentNode && container.contains?.(after)) after.insertAdjacentElement('afterend', panel);
      else container.appendChild(panel);
      panels.push(panel); refresh();
    };
    const begin = (editor, snapshot) => {
      const ua = String(window.navigator?.userAgent || '').slice(0, 512);
      const browser = /Edg\/(\d+)/.exec(ua) || /Firefox\/(\d+)/.exec(ua) || /(?:Chrome|CriOS)\/(\d+)/.exec(ua) || /Version\/(\d+).*Safari/.exec(ua);
      const now = Date.now();
      const record = clean({ startedAt: now, updatedAt: now, status: 'running', stage: 'start',
        revision: snapshot.revision, themeId: window.Shopify?.theme?.id,
        browser: browser ? (/Edg\//.test(ua) ? 'Edge' : /Firefox\//.test(ua) ? 'Firefox' : /Chrome|CriOS/.test(ua) ? 'Chrome' : 'Safari') : 'unknown', browserMajor: Number(browser?.[1]) || 0,
        platform: /iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && window.navigator?.maxTouchPoints > 1) ? 'iOS' : /Android/.test(ua) ? 'Android' : /Windows/.test(ua) ? 'Windows' : /Macintosh/.test(ua) ? 'macOS' : /Linux/.test(ua) ? 'Linux' : 'unknown',
        sourceWidth: editor.baseWidth, sourceHeight: editor.baseHeight, sourceBytes: Number(editor.sourceBlob?.size) || 0,
        sourceType: editor.sourceBlob?.type, provenance: editor.sourceRoute, outputWidth: snapshot.outputWidth, outputHeight: snapshot.outputHeight,
        textCount: editor.textLayers.length, textPresent: editor.textLayers.length > 0 });
      records.push(record); save();
      // The object handle binds every late callback to its own attempt, never to the latest attempt.
      return Object.freeze({ record });
    };
    const stage = (attempt, value) => {
      if (!attempt || !records.includes(attempt.record) || !stages.has(value)) return;
      const record = attempt.record; if (record.status === 'failure') return; record.stage = value; record.status = 'running'; record.updatedAt = Date.now();
      record.checkpoints.push({ stage: value, elapsedMs: Math.max(0, record.updatedAt - record.startedAt) });
      record.checkpoints = record.checkpoints.slice(-20); save();
    };
    const finish = (attempt, error = null, bytes = 0) => {
      if (!attempt || !records.includes(attempt.record)) return;
      const record = attempt.record; if (record.status === 'failure') return; record.status = error ? 'failure' : 'success'; record.updatedAt = Date.now();
      record.code = codes.has(error?.code) ? error.code : 'unknown'; record.name = names.has(error?.name) ? error.name : 'unknown';
      record.outputBytes = number(bytes) || record.outputBytes; save();
    };
    return Object.freeze({ begin, stage, finish, report, attach: (container, after) => { try { attach(container, after); } catch (_) { /* Recovery UI cannot block artwork or export. */ } } });
  })();
  window.DIMExportDiagnostics = exportDiagnostics;

  const imageInspector = (() => {
    const MAX_READ_BYTES = 1024 * 1024;
    const MAX_PNG_CHUNKS = 65536;
    const MAX_DEFLATE_BLOCKS = 65536;
    const PROGRESS_INTERVAL_MS = 100;
    const PROGRESS_CHECK_BYTES = 256 * 1024;
    const PROGRESS_YIELD_BYTES = 4 * 1024 * 1024;
    const RGBA_BYTES_PER_PIXEL = 4;
    // The envelope covers the phases the editor can actually overlap: the old
    // and candidate decoded sources during replacement, or one decoded source,
    // the governed export canvas and encoded source/output during export. It
    // admits the current 7500 x 10500 Apron source and output at full quality.
    const MAX_IMAGE_WORKING_BYTES = 1024 * 1024 * 1024;
    const PNG_SIGNATURE = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];
    const PNG_CRITICAL_CHUNKS = new Set(['IHDR', 'PLTE', 'IDAT', 'IEND']);
    const JPEG_SOF_MARKERS = new Set([0xc0, 0xc1, 0xc2]);
    const JPEG_UNSUPPORTED_SOF_MARKERS = new Set([0xc3, 0xc5, 0xc6, 0xc7, 0xc9, 0xca, 0xcb, 0xcd, 0xce, 0xcf]);
    const crcTable = new Uint32Array(256);
    for (let index = 0; index < crcTable.length; index += 1) {
      let value = index;
      for (let bit = 0; bit < 8; bit += 1) value = (value & 1) ? (0xedb88320 ^ (value >>> 1)) : (value >>> 1);
      crcTable[index] = value >>> 0;
    }

    const inspectionError = (code) => {
      const error = new Error('This image could not be decoded.');
      error.code = code;
      return error;
    };

    class InspectionProgress {
      constructor(observer) {
        this.observer = typeof observer === 'function' ? observer : null;
        this.active = Boolean(this.observer);
        this.phase = '';
        this.total = 0;
        this.loaded = 0;
        this.lastNotifiedAt = 0;
        this.lastYieldedAt = 0;
      }

      notify(force = false) {
        if (!this.active) return;
        const now = Date.now();
        if (!force && now - this.lastNotifiedAt < PROGRESS_INTERVAL_MS) return;
        this.lastNotifiedAt = now;
        try {
          const pending = this.observer(Object.freeze({ phase: this.phase, loaded: this.loaded, total: this.total }));
          if (pending && typeof pending.catch === 'function') pending.catch(() => {});
        } catch (_) {
          // Progress is observational and cannot change the validation result.
        }
      }

      begin(phase, total) {
        if (!this.active) return;
        this.phase = phase;
        this.total = exactPositiveInteger(total, 'IMAGE_PROGRESS_INVALID');
        this.loaded = 0;
        this.lastNotifiedAt = 0;
        this.lastYieldedAt = 0;
        this.notify(true);
      }

      async advance(phase, loaded, total) {
        if (!this.active || phase !== this.phase || total !== this.total) return;
        const next = Math.max(this.loaded, Math.min(this.total - 1, Math.max(0, Math.trunc(Number(loaded) || 0))));
        this.loaded = next;
        this.notify();
        if (this.loaded - this.lastYieldedAt < PROGRESS_YIELD_BYTES) return;
        this.lastYieldedAt = this.loaded;
        await new Promise((resolve) => setTimeout(resolve, 0));
      }

      complete(phase, total) {
        if (!this.active || phase !== this.phase || total !== this.total) return;
        this.loaded = this.total;
        this.notify(true);
      }

      stop() {
        this.active = false;
        this.observer = null;
      }
    }

    const exactPositiveInteger = (value, code = 'IMAGE_DIMENSIONS_INVALID') => {
      const number = Number(value);
      if (!Number.isSafeInteger(number) || number <= 0) throw inspectionError(code);
      return number;
    };

    const checkedPixels = (width, height) => {
      const exactWidth = exactPositiveInteger(width);
      const exactHeight = exactPositiveInteger(height);
      if (exactWidth > Math.floor(Number.MAX_SAFE_INTEGER / exactHeight)) throw inspectionError('IMAGE_DIMENSIONS_EXCESSIVE');
      return exactWidth * exactHeight;
    };

    class BlobReader {
      constructor(blob) {
        this.blob = blob;
        this.size = Number(blob?.size);
        this.cacheOffset = -1;
        this.cache = new Uint8Array(0);
      }

      async read(offset, length) {
        if (!Number.isSafeInteger(offset) || !Number.isSafeInteger(length) || offset < 0 || length < 0
          || length > MAX_READ_BYTES || offset + length > this.size) throw inspectionError('IMAGE_TRUNCATED');
        const cacheEnd = this.cacheOffset + this.cache.length;
        if (offset >= this.cacheOffset && offset + length <= cacheEnd) {
          return this.cache.subarray(offset - this.cacheOffset, offset - this.cacheOffset + length);
        }
        const readLength = Math.min(this.size - offset, Math.max(length, MAX_READ_BYTES));
        const bytes = new Uint8Array(await this.blob.slice(offset, offset + readLength).arrayBuffer());
        if (bytes.length !== readLength) throw inspectionError('IMAGE_TRUNCATED');
        this.cacheOffset = offset;
        this.cache = bytes;
        return bytes.subarray(0, length);
      }
    }

    const uint32 = (bytes, offset = 0) => (
      ((bytes[offset] * 0x1000000) + (bytes[offset + 1] << 16) + (bytes[offset + 2] << 8) + bytes[offset + 3]) >>> 0
    );

    const chunkType = (bytes) => String.fromCharCode(bytes[0], bytes[1], bytes[2], bytes[3]);

    const updateCrc = (crc, bytes) => {
      let next = crc;
      for (let index = 0; index < bytes.length; index += 1) next = crcTable[(next ^ bytes[index]) & 0xff] ^ (next >>> 8);
      return next >>> 0;
    };

    const validatePngCrc = async (reader, dataOffset, length, typeBytes, expected, progress) => {
      let crc = updateCrc(0xffffffff, typeBytes);
      let consumed = 0;
      while (consumed < length) {
        const batchLength = Math.min(MAX_READ_BYTES, length - consumed);
        crc = updateCrc(crc, await reader.read(dataOffset + consumed, batchLength));
        consumed += batchLength;
        if (progress.active) await progress.advance('structure', dataOffset + consumed, reader.size);
      }
      if (((crc ^ 0xffffffff) >>> 0) !== expected) throw inspectionError('PNG_CRC_INVALID');
      if (progress.active) await progress.advance('structure', dataOffset + length + 4, reader.size);
    };

    const validPngBitDepth = (bitDepth, colourType) => ({
      0: [1, 2, 4, 8, 16],
      2: [8, 16],
      3: [1, 2, 4, 8],
      4: [8, 16],
      6: [8, 16]
    })[colourType]?.includes(bitDepth) === true;

    const readTiffOrientation = async (reader, start, length) => {
      if (length < 8) throw inspectionError('IMAGE_EXIF_INVALID');
      const header = await reader.read(start, 8);
      const littleEndian = header[0] === 0x49 && header[1] === 0x49;
      if (!littleEndian && !(header[0] === 0x4d && header[1] === 0x4d)) throw inspectionError('IMAGE_EXIF_INVALID');
      const word = (bytes, offset) => littleEndian
        ? bytes[offset] + (bytes[offset + 1] << 8)
        : (bytes[offset] << 8) + bytes[offset + 1];
      const long = (bytes, offset) => littleEndian
        ? ((bytes[offset]) + (bytes[offset + 1] << 8) + (bytes[offset + 2] << 16) + (bytes[offset + 3] * 0x1000000)) >>> 0
        : ((bytes[offset] * 0x1000000) + (bytes[offset + 1] << 16) + (bytes[offset + 2] << 8) + bytes[offset + 3]) >>> 0;
      if (word(header, 2) !== 42) throw inspectionError('IMAGE_EXIF_INVALID');
      const directoryOffset = long(header, 4);
      if (directoryOffset < 8 || directoryOffset + 2 > length) throw inspectionError('IMAGE_EXIF_INVALID');
      const countBytes = await reader.read(start + directoryOffset, 2);
      const count = word(countBytes, 0);
      if (count > 4096 || directoryOffset + 2 + count * 12 > length) throw inspectionError('IMAGE_EXIF_INVALID');
      const entries = await reader.read(start + directoryOffset + 2, count * 12);
      let orientation = 1;
      let found = false;
      for (let index = 0; index < count; index += 1) {
        const offset = index * 12;
        if (word(entries, offset) !== 0x0112) continue;
        if (found || word(entries, offset + 2) !== 3 || long(entries, offset + 4) !== 1) {
          throw inspectionError('IMAGE_EXIF_INVALID');
        }
        orientation = word(entries, offset + 8);
        if (orientation < 1 || orientation > 8) throw inspectionError('IMAGE_EXIF_INVALID');
        found = true;
      }
      return orientation;
    };

    const orientedDimensions = (width, height, orientation) => (
      orientation >= 5 && orientation <= 8
        ? { width: height, height: width }
        : { width, height }
    );

    const pngScanlines = (width, height, bitDepth, colourType, interlace) => {
      const channels = { 0: 1, 2: 3, 3: 1, 4: 2, 6: 4 }[colourType];
      const passGeometry = interlace === 1
        ? [[0, 0, 8, 8], [4, 0, 8, 8], [0, 4, 4, 8], [2, 0, 4, 4], [0, 2, 2, 4], [1, 0, 2, 2], [0, 1, 1, 2]]
        : [[0, 0, 1, 1]];
      return passGeometry.map(([startX, startY, stepX, stepY]) => {
        const passWidth = width <= startX ? 0 : Math.ceil((width - startX) / stepX);
        const rows = height <= startY ? 0 : Math.ceil((height - startY) / stepY);
        return { rowBytes: Math.ceil((passWidth * channels * bitDepth) / 8), rows: passWidth ? rows : 0 };
      }).filter((pass) => pass.rows > 0);
    };

    const pngScanlineBytes = (scanlines) => scanlines.reduce(
      (total, pass) => total + (pass.rowBytes + 1) * pass.rows,
      0
    );

    class PngCompressedSource {
      constructor(reader, ranges) {
        this.reader = reader;
        this.ranges = ranges;
        this.rangeIndex = 0;
        this.rangeOffset = 0;
        this.bytes = new Uint8Array(0);
        this.offset = 0;
      }

      available() {
        return this.bytes.length - this.offset;
      }

      async refill(minimum = 1) {
        const remaining = this.bytes.subarray(this.offset);
        if (remaining.length >= minimum) return true;
        const next = new Uint8Array(remaining.length + MAX_READ_BYTES);
        next.set(remaining);
        let length = remaining.length;
        while (length < next.length && this.rangeIndex < this.ranges.length) {
          const range = this.ranges[this.rangeIndex];
          if (this.rangeOffset >= range.length) {
            this.rangeIndex += 1;
            this.rangeOffset = 0;
            continue;
          }
          const batchLength = Math.min(next.length - length, range.length - this.rangeOffset);
          next.set(await this.reader.read(range.offset + this.rangeOffset, batchLength), length);
          length += batchLength;
          this.rangeOffset += batchLength;
        }
        this.bytes = next.subarray(0, length);
        this.offset = 0;
        return length >= minimum;
      }

      readByte() {
        if (this.offset >= this.bytes.length) throw inspectionError('PNG_IMAGE_DATA_INVALID');
        return this.bytes[this.offset++];
      }
    }

    class DeflateBits {
      constructor(source) {
        this.source = source;
        this.value = 0;
        this.count = 0;
      }

      read(count) {
        while (this.count < count) {
          this.value = (this.value | (this.source.readByte() << this.count)) >>> 0;
          this.count += 8;
        }
        const mask = count === 32 ? 0xffffffff : ((1 << count) - 1);
        const result = this.value & mask;
        this.value >>>= count;
        this.count -= count;
        return result;
      }

      align() {
        this.value = 0;
        this.count = 0;
      }
    }

    const reverseDeflateCode = (value, length) => {
      let reversed = 0;
      for (let index = 0; index < length; index += 1) {
        reversed = (reversed << 1) | ((value >>> index) & 1);
      }
      return reversed;
    };

    const buildDeflateTree = (lengths, { requireEnd = false, allowEmpty = false, allowIncompleteSingle = false } = {}) => {
      const counts = new Uint16Array(16);
      let symbols = 0;
      let maximumLength = 0;
      for (const length of lengths) {
        if (length < 0 || length > 15) throw inspectionError('PNG_IMAGE_DATA_INVALID');
        if (length) {
          counts[length] += 1;
          symbols += 1;
          maximumLength = Math.max(maximumLength, length);
        }
      }
      if (!symbols) {
        if (allowEmpty) return null;
        throw inspectionError('PNG_IMAGE_DATA_INVALID');
      }
      if (requireEnd && !lengths[256]) throw inspectionError('PNG_IMAGE_DATA_INVALID');
      let remaining = 1;
      for (let length = 1; length <= 15; length += 1) {
        remaining = remaining * 2 - counts[length];
        if (remaining < 0) throw inspectionError('PNG_IMAGE_DATA_INVALID');
      }
      if (remaining > 0 && !(allowIncompleteSingle && maximumLength === 1)) {
        throw inspectionError('PNG_IMAGE_DATA_INVALID');
      }
      const nextCodes = new Uint16Array(16);
      let code = 0;
      for (let length = 1; length <= 15; length += 1) {
        code = (code + counts[length - 1]) << 1;
        nextCodes[length] = code;
      }
      const tables = Array.from({ length: 16 }, () => new Map());
      lengths.forEach((length, symbol) => {
        if (!length) return;
        tables[length].set(reverseDeflateCode(nextCodes[length]++, length), symbol);
      });
      return { tables, maximumLength };
    };

    const decodeDeflateSymbol = (bits, tree) => {
      let code = 0;
      for (let length = 1; length <= tree.maximumLength; length += 1) {
        code |= bits.read(1) << (length - 1);
        const symbol = tree.tables[length].get(code);
        if (symbol !== undefined) return symbol;
      }
      throw inspectionError('PNG_IMAGE_DATA_INVALID');
    };

    const fixedDeflateTrees = () => {
      const literalLengths = new Uint8Array(288);
      literalLengths.fill(8, 0, 144);
      literalLengths.fill(9, 144, 256);
      literalLengths.fill(7, 256, 280);
      literalLengths.fill(8, 280);
      const distanceLengths = new Uint8Array(32);
      distanceLengths.fill(5);
      return {
        literal: buildDeflateTree(literalLengths, { requireEnd: true }),
        distance: buildDeflateTree(distanceLengths)
      };
    };

    const dynamicDeflateTrees = async (source, bits) => {
      if (source.available() < 8) await source.refill(8);
      const literalCount = bits.read(5) + 257;
      const distanceCount = bits.read(5) + 1;
      const codeLengthCount = bits.read(4) + 4;
      if (literalCount > 286 || distanceCount > 32) throw inspectionError('PNG_IMAGE_DATA_INVALID');
      const order = [16, 17, 18, 0, 8, 7, 9, 6, 10, 5, 11, 4, 12, 3, 13, 2, 14, 1, 15];
      const codeLengths = new Uint8Array(19);
      for (let index = 0; index < codeLengthCount; index += 1) codeLengths[order[index]] = bits.read(3);
      const codeTree = buildDeflateTree(codeLengths);
      const lengths = [];
      while (lengths.length < literalCount + distanceCount) {
        if (source.available() < 8) await source.refill(8);
        const symbol = decodeDeflateSymbol(bits, codeTree);
        if (symbol <= 15) {
          lengths.push(symbol);
          continue;
        }
        let repeat;
        let value = 0;
        if (symbol === 16) {
          if (!lengths.length) throw inspectionError('PNG_IMAGE_DATA_INVALID');
          repeat = bits.read(2) + 3;
          value = lengths[lengths.length - 1];
        } else if (symbol === 17) {
          repeat = bits.read(3) + 3;
        } else if (symbol === 18) {
          repeat = bits.read(7) + 11;
        } else {
          throw inspectionError('PNG_IMAGE_DATA_INVALID');
        }
        if (lengths.length + repeat > literalCount + distanceCount) throw inspectionError('PNG_IMAGE_DATA_INVALID');
        while (repeat-- > 0) lengths.push(value);
      }
      return {
        literal: buildDeflateTree(lengths.slice(0, literalCount), { requireEnd: true, allowIncompleteSingle: true }),
        distance: buildDeflateTree(lengths.slice(literalCount), { allowEmpty: true, allowIncompleteSingle: true })
      };
    };

    class PngInflatedOutput {
      constructor(scanlines, windowLimit) {
        this.scanlines = scanlines;
        this.windowLimit = windowLimit;
        this.passIndex = 0;
        this.rowIndex = 0;
        this.rowOffset = 0;
        this.count = 0;
        this.history = new Uint8Array(32768);
        this.adlerA = 1;
        this.adlerB = 0;
        this.adlerPending = 0;
      }

      write(value) {
        const pass = this.scanlines[this.passIndex];
        if (!pass) throw inspectionError('PNG_IMAGE_DATA_EXCESSIVE');
        if (this.rowOffset === 0 && value > 4) throw inspectionError('PNG_FILTER_INVALID');
        this.history[this.count & 32767] = value;
        this.count += 1;
        this.adlerA += value;
        this.adlerB += this.adlerA;
        this.adlerPending += 1;
        if (this.adlerPending === 5552) {
          this.adlerA %= 65521;
          this.adlerB %= 65521;
          this.adlerPending = 0;
        }
        this.rowOffset += 1;
        if (this.rowOffset === pass.rowBytes + 1) {
          this.rowOffset = 0;
          this.rowIndex += 1;
          if (this.rowIndex === pass.rows) {
            this.passIndex += 1;
            this.rowIndex = 0;
          }
        }
      }

      copy(distance, length) {
        if (!distance || distance > this.windowLimit || distance > this.count) throw inspectionError('PNG_IMAGE_DATA_INVALID');
        while (length-- > 0) this.write(this.history[(this.count - distance) & 32767]);
      }

      checksum() {
        this.adlerA %= 65521;
        this.adlerB %= 65521;
        return ((this.adlerB << 16) | this.adlerA) >>> 0;
      }

      complete() {
        if (this.passIndex !== this.scanlines.length || this.rowIndex !== 0 || this.rowOffset !== 0) {
          throw inspectionError('PNG_IMAGE_DATA_TRUNCATED');
        }
      }
    }

    const LENGTH_BASE = [3, 4, 5, 6, 7, 8, 9, 10, 11, 13, 15, 17, 19, 23, 27, 31, 35, 43, 51, 59, 67, 83, 99, 115, 131, 163, 195, 227, 258];
    const LENGTH_EXTRA = [0, 0, 0, 0, 0, 0, 0, 0, 1, 1, 1, 1, 2, 2, 2, 2, 3, 3, 3, 3, 4, 4, 4, 4, 5, 5, 5, 5, 0];
    const DISTANCE_BASE = [1, 2, 3, 4, 5, 7, 9, 13, 17, 25, 33, 49, 65, 97, 129, 193, 257, 385, 513, 769, 1025, 1537, 2049, 3073, 4097, 6145, 8193, 12289, 16385, 24577];
    const DISTANCE_EXTRA = [0, 0, 0, 0, 1, 1, 2, 2, 3, 3, 4, 4, 5, 5, 6, 6, 7, 7, 8, 8, 9, 9, 10, 10, 11, 11, 12, 12, 13, 13];

    const validatePngImageDataPortable = async (reader, ranges, scanlines, progress, pixelTotal) => {
      const source = new PngCompressedSource(reader, ranges);
      await source.refill(8);
      const compression = source.readByte();
      const flags = source.readByte();
      if ((compression & 0x0f) !== 8 || (compression >>> 4) > 7
        || ((compression << 8) + flags) % 31 !== 0 || (flags & 0x20)) {
        throw inspectionError('PNG_IMAGE_DATA_INVALID');
      }
      const bits = new DeflateBits(source);
      const output = new PngInflatedOutput(scanlines, 1 << ((compression >>> 4) + 8));
      let nextPixelProgress = PROGRESS_CHECK_BYTES;
      let finalBlock = false;
      let blockCount = 0;
      while (!finalBlock) {
        blockCount += 1;
        if (blockCount > MAX_DEFLATE_BLOCKS) throw inspectionError('PNG_IMAGE_DATA_EXCESSIVE');
        if (source.available() < 8) await source.refill(8);
        finalBlock = bits.read(1) === 1;
        const blockType = bits.read(2);
        if (blockType === 0) {
          bits.align();
          if (source.available() < 4) await source.refill(4);
          const length = source.readByte() | (source.readByte() << 8);
          const complement = source.readByte() | (source.readByte() << 8);
          if (((length ^ 0xffff) & 0xffff) !== complement) throw inspectionError('PNG_IMAGE_DATA_INVALID');
          let remaining = length;
          while (remaining > 0) {
            const batchLength = Math.min(remaining, PROGRESS_CHECK_BYTES);
            for (let index = 0; index < batchLength; index += 1) {
              if (!source.available()) await source.refill(1);
              output.write(source.readByte());
            }
            remaining -= batchLength;
            if (progress.active && output.count >= nextPixelProgress) {
              nextPixelProgress = output.count + PROGRESS_CHECK_BYTES;
              await progress.advance('pixels', output.count, pixelTotal);
            }
          }
          continue;
        }
        if (blockType === 3) throw inspectionError('PNG_IMAGE_DATA_INVALID');
        const trees = blockType === 1 ? fixedDeflateTrees() : await dynamicDeflateTrees(source, bits);
        while (true) {
          if (source.available() < 8) await source.refill(8);
          const symbol = decodeDeflateSymbol(bits, trees.literal);
          if (symbol < 256) {
            output.write(symbol);
            if (progress.active && output.count >= nextPixelProgress) {
              nextPixelProgress = output.count + PROGRESS_CHECK_BYTES;
              await progress.advance('pixels', output.count, pixelTotal);
            }
            continue;
          }
          if (symbol === 256) break;
          const lengthIndex = symbol - 257;
          if (lengthIndex < 0 || lengthIndex >= LENGTH_BASE.length) throw inspectionError('PNG_IMAGE_DATA_INVALID');
          const length = LENGTH_BASE[lengthIndex] + bits.read(LENGTH_EXTRA[lengthIndex]);
          if (!trees.distance) throw inspectionError('PNG_IMAGE_DATA_INVALID');
          const distanceSymbol = decodeDeflateSymbol(bits, trees.distance);
          if (distanceSymbol >= DISTANCE_BASE.length) throw inspectionError('PNG_IMAGE_DATA_INVALID');
          const distance = DISTANCE_BASE[distanceSymbol] + bits.read(DISTANCE_EXTRA[distanceSymbol]);
          output.copy(distance, length);
          if (progress.active && output.count >= nextPixelProgress) {
            nextPixelProgress = output.count + PROGRESS_CHECK_BYTES;
            await progress.advance('pixels', output.count, pixelTotal);
          }
        }
      }
      bits.align();
      if (source.available() < 4) await source.refill(4);
      const expected = ((source.readByte() * 0x1000000) + (source.readByte() << 16)
        + (source.readByte() << 8) + source.readByte()) >>> 0;
      if (output.checksum() !== expected) throw inspectionError('PNG_IMAGE_DATA_INVALID');
      output.complete();
      if (progress.active) await progress.advance('pixels', output.count, pixelTotal);
      if (source.available() || await source.refill(1)) throw inspectionError('PNG_IMAGE_DATA_INVALID');
      return true;
    };

    const validatePngImageData = async (reader, ranges, scanlines, progress, pixelTotal) => {
      if (typeof window.DecompressionStream !== 'function' || typeof window.ReadableStream !== 'function') {
        return validatePngImageDataPortable(reader, ranges, scanlines, progress, pixelTotal);
      }
      let rangeIndex = 0;
      let rangeOffset = 0;
      const compressed = new window.ReadableStream({
        async pull(controller) {
          while (rangeIndex < ranges.length && rangeOffset >= ranges[rangeIndex].length) {
            rangeIndex += 1;
            rangeOffset = 0;
          }
          if (rangeIndex >= ranges.length) {
            controller.close();
            return;
          }
          const range = ranges[rangeIndex];
          const length = Math.min(MAX_READ_BYTES, range.length - rangeOffset);
          controller.enqueue(await reader.read(range.offset + rangeOffset, length));
          rangeOffset += length;
        }
      });
      let decompressor;
      try {
        decompressor = new window.DecompressionStream('deflate');
      } catch (_) {
        return validatePngImageDataPortable(reader, ranges, scanlines, progress, pixelTotal);
      }
      let passIndex = 0;
      let rowIndex = 0;
      let rowOffset = 0;
      let pixelBytes = 0;
      const decompressed = compressed.pipeThrough(decompressor).getReader();
      try {
        while (true) {
          const { value, done } = await decompressed.read();
          if (done) break;
          for (let index = 0; index < value.length; index += 1) {
            const pass = scanlines[passIndex];
            if (!pass) throw inspectionError('PNG_IMAGE_DATA_EXCESSIVE');
            if (rowOffset === 0 && value[index] > 4) throw inspectionError('PNG_FILTER_INVALID');
            rowOffset += 1;
            if (rowOffset === pass.rowBytes + 1) {
              rowOffset = 0;
              rowIndex += 1;
              if (rowIndex === pass.rows) {
                passIndex += 1;
                rowIndex = 0;
              }
            }
          }
          pixelBytes += value.length;
          if (progress.active) await progress.advance('pixels', pixelBytes, pixelTotal);
        }
      } catch (error) {
        try {
          await decompressed.cancel(error);
        } catch (_) {
          // The decompressor may already have aborted after reporting corrupt data.
        }
        if (String(error?.code || '').startsWith('PNG_')) throw error;
        throw inspectionError('PNG_IMAGE_DATA_INVALID');
      } finally {
        decompressed.releaseLock();
      }
      if (passIndex !== scanlines.length || rowIndex !== 0 || rowOffset !== 0) {
        throw inspectionError('PNG_IMAGE_DATA_TRUNCATED');
      }
      return true;
    };

    const inspectPng = async (reader, progress) => {
      if (reader.size < 45) throw inspectionError('IMAGE_TRUNCATED');
      const signature = await reader.read(0, PNG_SIGNATURE.length);
      if (!PNG_SIGNATURE.every((value, index) => signature[index] === value)) throw inspectionError('IMAGE_FORMAT_INVALID');
      if (progress.active) await progress.advance('structure', PNG_SIGNATURE.length, reader.size);
      let offset = PNG_SIGNATURE.length;
      let chunks = 0;
      let width = 0;
      let height = 0;
      let colourType = -1;
      let sawPalette = false;
      let sawData = false;
      let dataBytes = 0;
      const dataRanges = [];
      let dataEnded = false;
      let sawEnd = false;
      let sawExif = false;
      let orientation = 1;
      while (offset < reader.size) {
        chunks += 1;
        if (chunks > MAX_PNG_CHUNKS) throw inspectionError('PNG_STRUCTURE_EXCESSIVE');
        const header = await reader.read(offset, 8);
        const length = uint32(header);
        const typeBytes = header.subarray(4, 8);
        const type = chunkType(typeBytes);
        if (![...typeBytes].every((value) => (value >= 65 && value <= 90) || (value >= 97 && value <= 122))) {
          throw inspectionError('PNG_CHUNK_INVALID');
        }
        const dataOffset = offset + 8;
        const crcOffset = dataOffset + length;
        if (!Number.isSafeInteger(crcOffset) || crcOffset + 4 > reader.size) throw inspectionError('IMAGE_TRUNCATED');
        const expectedCrc = uint32(await reader.read(crcOffset, 4));
        await validatePngCrc(reader, dataOffset, length, typeBytes, expectedCrc, progress);
        if (chunks === 1) {
          if (type !== 'IHDR' || length !== 13) throw inspectionError('PNG_HEADER_INVALID');
          const headerData = await reader.read(dataOffset, 13);
          width = uint32(headerData);
          height = uint32(headerData, 4);
          if (checkedPixels(width, height) * RGBA_BYTES_PER_PIXEL > MAX_IMAGE_WORKING_BYTES) {
            throw inspectionError('IMAGE_DIMENSIONS_EXCESSIVE');
          }
          const bitDepth = headerData[8];
          colourType = headerData[9];
          if (!validPngBitDepth(bitDepth, colourType) || headerData[10] !== 0 || headerData[11] !== 0 || headerData[12] > 1) {
            throw inspectionError('PNG_UNSUPPORTED');
          }
        } else if (type === 'IHDR') {
          throw inspectionError('PNG_HEADER_INVALID');
        } else if (type === 'PLTE') {
          if (sawPalette || sawData || colourType === 0 || colourType === 4 || !length || length % 3 || length > 768) {
            throw inspectionError('PNG_PALETTE_INVALID');
          }
          sawPalette = true;
        } else if (type === 'IDAT') {
          if (dataEnded || (colourType === 3 && !sawPalette)) throw inspectionError('PNG_DATA_INVALID');
          sawData = true;
          dataBytes += length;
          if (length) dataRanges.push({ offset: dataOffset, length });
        } else if (type === 'eXIf') {
          if (sawExif || sawData) throw inspectionError('IMAGE_EXIF_INVALID');
          orientation = await readTiffOrientation(reader, dataOffset, length);
          sawExif = true;
        } else if (type === 'IEND') {
          if (!sawData || !dataBytes || length !== 0) throw inspectionError('PNG_END_INVALID');
          sawEnd = true;
        } else {
          if (sawData) dataEnded = true;
          if (type === 'acTL') throw inspectionError('PNG_ANIMATION_UNSUPPORTED');
          if (typeBytes[0] >= 65 && typeBytes[0] <= 90 && !PNG_CRITICAL_CHUNKS.has(type)) {
            throw inspectionError('PNG_CRITICAL_CHUNK_UNSUPPORTED');
          }
        }
        offset = crcOffset + 4;
        if (sawEnd) {
          if (offset !== reader.size) throw inspectionError('PNG_TRAILING_DATA');
          break;
        }
      }
      if (!sawEnd) throw inspectionError('IMAGE_TRUNCATED');
      const imageHeader = await reader.read(16, 13);
      progress.complete('structure', reader.size);
      const scanlines = pngScanlines(width, height, imageHeader[8], colourType, imageHeader[12]);
      const pixelTotal = pngScanlineBytes(scanlines);
      progress.begin('pixels', pixelTotal);
      const semantic = await validatePngImageData(
        reader,
        dataRanges,
        scanlines,
        progress,
        pixelTotal
      );
      progress.complete('pixels', pixelTotal);
      const oriented = orientedDimensions(width, height, orientation);
      return { type: 'image/png', width: oriented.width, height: oriented.height, encodedWidth: width, encodedHeight: height, orientation, semantic };
    };

    const nextJpegScanMarker = async (reader, startOffset, progress) => {
      let offset = startOffset;
      while (offset < reader.size) {
        const length = Math.min(MAX_READ_BYTES, reader.size - offset);
        const bytes = await reader.read(offset, length);
        let continueOffset = offset + length;
        for (let index = 0; index < bytes.length; index += 1) {
          if (bytes[index] !== 0xff) continue;
          let markerOffset = offset + index + 1;
          let marker = markerOffset < offset + bytes.length
            ? bytes[markerOffset - offset]
            : (await reader.read(markerOffset, 1))[0];
          markerOffset += 1;
          while (marker === 0xff) {
            marker = markerOffset < offset + bytes.length
              ? bytes[markerOffset - offset]
              : (await reader.read(markerOffset, 1))[0];
            markerOffset += 1;
          }
          if (marker === 0x00 || (marker >= 0xd0 && marker <= 0xd7)) {
            if (markerOffset < offset + bytes.length) {
              index = markerOffset - offset - 1;
            } else {
              continueOffset = markerOffset;
              break;
            }
            continue;
          }
          if (progress.active) await progress.advance('structure', markerOffset, reader.size);
          return { marker, offset: markerOffset };
        }
        if (progress.active) await progress.advance('structure', continueOffset, reader.size);
        offset = continueOffset;
      }
      throw inspectionError('IMAGE_TRUNCATED');
    };

    const inspectJpeg = async (reader, progress) => {
      if (reader.size < 4) throw inspectionError('IMAGE_TRUNCATED');
      const start = await reader.read(0, 2);
      if (start[0] !== 0xff || start[1] !== 0xd8) throw inspectionError('IMAGE_FORMAT_INVALID');
      if (progress.active) await progress.advance('structure', 2, reader.size);
      let offset = 2;
      let width = 0;
      let height = 0;
      let sawScan = false;
      let sawExif = false;
      let orientation = 1;
      let pendingMarker = null;
      while (offset < reader.size) {
        let marker;
        if (pendingMarker !== null) {
          marker = pendingMarker;
          pendingMarker = null;
        } else {
          const prefix = (await reader.read(offset, 1))[0];
          offset += 1;
          if (prefix !== 0xff) throw inspectionError('JPEG_MARKER_INVALID');
          marker = (await reader.read(offset, 1))[0];
          offset += 1;
          while (marker === 0xff) {
            marker = (await reader.read(offset, 1))[0];
            offset += 1;
          }
        }
        if (marker === 0xd9) {
          if (!width || !height || !sawScan) throw inspectionError('JPEG_STRUCTURE_INVALID');
          progress.complete('structure', reader.size);
          const oriented = orientedDimensions(width, height, orientation);
          return { type: 'image/jpeg', width: oriented.width, height: oriented.height, encodedWidth: width, encodedHeight: height, orientation };
        }
        if (marker === 0x00 || marker === 0xd8 || marker === 0x01 || (marker >= 0xd0 && marker <= 0xd7)) {
          throw inspectionError('JPEG_MARKER_INVALID');
        }
        if (offset + 2 > reader.size) throw inspectionError('IMAGE_TRUNCATED');
        const lengthBytes = await reader.read(offset, 2);
        const length = (lengthBytes[0] << 8) + lengthBytes[1];
        if (length < 2 || offset + length > reader.size) throw inspectionError('IMAGE_TRUNCATED');
        const dataOffset = offset + 2;
        const dataLength = length - 2;
        if (marker === 0xe1 && dataLength >= 6) {
          const prefix = await reader.read(dataOffset, 6);
          const isExif = prefix[0] === 0x45 && prefix[1] === 0x78 && prefix[2] === 0x69
            && prefix[3] === 0x66 && prefix[4] === 0 && prefix[5] === 0;
          if (isExif) {
            if (sawExif) throw inspectionError('IMAGE_EXIF_INVALID');
            orientation = await readTiffOrientation(reader, dataOffset + 6, dataLength - 6);
            sawExif = true;
          }
        }
        if (JPEG_UNSUPPORTED_SOF_MARKERS.has(marker)) throw inspectionError('JPEG_UNSUPPORTED');
        if (JPEG_SOF_MARKERS.has(marker)) {
          if (width || dataLength < 6) throw inspectionError('JPEG_FRAME_INVALID');
          const frame = await reader.read(dataOffset, 6);
          if (frame[0] !== 8) throw inspectionError('JPEG_UNSUPPORTED');
          height = (frame[1] << 8) + frame[2];
          width = (frame[3] << 8) + frame[4];
          const components = frame[5];
          if (!components || components > 4 || dataLength !== 6 + components * 3) throw inspectionError('JPEG_FRAME_INVALID');
          checkedPixels(width, height);
        }
        offset += length;
        if (progress.active) await progress.advance('structure', offset, reader.size);
        if (marker === 0xda) {
          if (!width || !height || dataLength < 6) throw inspectionError('JPEG_SCAN_INVALID');
          sawScan = true;
          const next = await nextJpegScanMarker(reader, offset, progress);
          marker = next.marker;
          offset = next.offset;
          if (marker === 0xd9) {
            if (!width || !height || !sawScan) throw inspectionError('JPEG_STRUCTURE_INVALID');
            progress.complete('structure', reader.size);
            const oriented = orientedDimensions(width, height, orientation);
            return { type: 'image/jpeg', width: oriented.width, height: oriented.height, encodedWidth: width, encodedHeight: height, orientation };
          }
          pendingMarker = marker;
        }
      }
      throw inspectionError('IMAGE_TRUNCATED');
    };

    const inspect = async (blob, { expectedMime = '', maximumBytes = 0, onProgress = null } = {}) => {
      if (!(blob instanceof Blob) || !Number.isSafeInteger(Number(blob.size)) || blob.size <= 0) {
        throw inspectionError('IMAGE_BLOB_INVALID');
      }
      if (maximumBytes && blob.size > maximumBytes) throw inspectionError('IMAGE_BYTES_EXCESSIVE');
      const progress = new InspectionProgress(onProgress);
      progress.begin('structure', blob.size);
      const reader = new BlobReader(blob);
      try {
        const signature = await reader.read(0, Math.min(8, reader.size));
        let result;
        if (signature.length === 8 && PNG_SIGNATURE.every((value, index) => signature[index] === value)) {
          result = await inspectPng(reader, progress);
        } else if (signature.length >= 3 && signature[0] === 0xff && signature[1] === 0xd8 && signature[2] === 0xff) {
          result = await inspectJpeg(reader, progress);
        } else {
          throw inspectionError('IMAGE_FORMAT_INVALID');
        }
        if (expectedMime && result.type !== expectedMime) throw inspectionError('IMAGE_FORMAT_INVALID');
        return Object.freeze({
          blob,
          type: result.type,
          width: result.width,
          height: result.height,
          encodedWidth: result.encodedWidth,
          encodedHeight: result.encodedHeight,
          orientation: result.orientation,
          semantic: result.semantic === true,
          size: blob.size
        });
      } finally {
        progress.stop();
      }
    };

    const surfaceBytes = (width, height) => checkedPixels(width, height) * RGBA_BYTES_PER_PIXEL;
    const maximumPngBytes = (width, height) => {
      const pixelBytes = surfaceBytes(width, height);
      const scanlineBytes = pixelBytes + exactPositiveInteger(height);
      // zlib's conservative compressBound formula covers ordinary deflate block
      // sizing for incompressible RGBA data. The remaining allowance covers the
      // inspector's maximum chunk framing plus bounded canvas encoder metadata.
      const deflateBytes = scanlineBytes + Math.floor(scanlineBytes / 4096)
        + Math.floor(scanlineBytes / 16384) + Math.floor(scanlineBytes / 33554432) + 13;
      return 8 + 13 + deflateBytes + MAX_PNG_CHUNKS * 12 + MAX_READ_BYTES;
    };
    const optionalSurfaceBytes = (width, height) => Number(width) > 0 && Number(height) > 0 ? surfaceBytes(width, height) : 0;
    const optionalEncodedBytes = (value) => Number.isSafeInteger(Number(value)) && Number(value) > 0 ? Number(value) : 0;

    const admit = (inspection, {
      outputWidth = 0,
      outputHeight = 0,
      retainedWidth = 0,
      retainedHeight = 0,
      retainedEncodedBytes = 0
    } = {}) => {
      if (!inspection?.blob || inspection.blob.size !== inspection.size) throw inspectionError('IMAGE_INSPECTION_STALE');
      const candidateSurface = surfaceBytes(inspection.width, inspection.height);
      const candidateEncoded = optionalEncodedBytes(inspection.size);
      const decodeBytes = candidateSurface + candidateEncoded;
      const retainedSurface = optionalSurfaceBytes(retainedWidth, retainedHeight);
      const replacementBytes = decodeBytes + retainedSurface + optionalEncodedBytes(retainedEncodedBytes);
      const outputSurface = optionalSurfaceBytes(outputWidth, outputHeight);
      const exportBytes = outputSurface
        ? decodeBytes + outputSurface + maximumPngBytes(outputWidth, outputHeight)
        : decodeBytes;
      const requiredBytes = Math.max(decodeBytes, replacementBytes, exportBytes);
      if (!Number.isSafeInteger(requiredBytes) || requiredBytes > MAX_IMAGE_WORKING_BYTES) {
        throw inspectionError('IMAGE_MEMORY_LIMIT');
      }
      return Object.freeze({ requiredBytes, limitBytes: MAX_IMAGE_WORKING_BYTES });
    };

    const admitComposition = ({ outputWidth, outputHeight, retainedWidth = 0, retainedHeight = 0, retainedEncodedBytes = 0 }) => {
      const requiredBytes = surfaceBytes(outputWidth, outputHeight) + maximumPngBytes(outputWidth, outputHeight)
        + optionalSurfaceBytes(retainedWidth, retainedHeight) + optionalEncodedBytes(retainedEncodedBytes);
      if (!Number.isSafeInteger(requiredBytes) || requiredBytes > MAX_IMAGE_WORKING_BYTES) throw inspectionError('IMAGE_MEMORY_LIMIT');
      return Object.freeze({ requiredBytes, limitBytes: MAX_IMAGE_WORKING_BYTES });
    };
    const admitEncodedOutput = (inspection, {
      sourceWidth,
      sourceHeight,
      sourceEncodedBytes = 0,
      outputWidth,
      outputHeight,
      includeDecodedOutput = false,
      composition = undefined
    }) => {
      if (inspection?.type !== 'image/png' || inspection.width !== outputWidth || inspection.height !== outputHeight) {
        throw inspectionError('PNG_OUTPUT_INVALID');
      }
      if (inspection.size > maximumPngBytes(outputWidth, outputHeight)) throw inspectionError('PNG_OUTPUT_EXCESSIVE');
      const words = composition !== undefined && window.DIMSceneIdentity?.validateComposition(composition);
      if (words && (sourceWidth !== 0 || sourceHeight !== 0 || sourceEncodedBytes !== 0)) throw inspectionError('PNG_OUTPUT_INVALID');
      const requiredBytes = (words ? 0 : surfaceBytes(sourceWidth, sourceHeight)) + optionalEncodedBytes(sourceEncodedBytes)
        + inspection.size + surfaceBytes(outputWidth, outputHeight)
        + (includeDecodedOutput ? surfaceBytes(outputWidth, outputHeight) : 0);
      if (!Number.isSafeInteger(requiredBytes) || requiredBytes > MAX_IMAGE_WORKING_BYTES) {
        throw inspectionError('IMAGE_MEMORY_LIMIT');
      }
      return Object.freeze({ requiredBytes, limitBytes: MAX_IMAGE_WORKING_BYTES });
    };

    return Object.freeze({ inspect, admit, admitComposition, admitEncodedOutput });
  })();

  window.DreamItImageInspector = imageInspector;

  const toNumber = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
  const MAX_SCALE = 5;
  const SCALE_EPSILON = 0.000001;

  const asInt = (value) => {
    const parsed = Math.trunc(Math.floor(toNumber(value)));
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const quarterTurns = (value) => {
    const normalized = ((Number(value) || 0) % 4 + 4) % 4;
    return normalized;
  };

  const emit = (root, name, detail = {}) => {
    root?.dispatchEvent(new CustomEvent(name, {
      bubbles: true,
      composed: true,
      detail
    }));
  };

  const effectiveDimensions = (width, height, rotation) => {
    const normalisedRotation = quarterTurns(rotation);
    return normalisedRotation % 2 ? { width: height, height: width } : { width, height };
  };

  const minScale = (fitScale) => Math.max(0.01, fitScale * 0.25);

  const clampPosition = (position, rendered, board) => {
    const minimumVisible = Math.min(rendered * 0.1, board * 0.1);
    const minimum = minimumVisible - rendered;
    const maximum = board - minimumVisible;
    return clamp(position, minimum, maximum);
  };

  const decodeImageSource = async (blob) => {
    if (window.createImageBitmap) {
      return createImageBitmap(blob, {
        imageOrientation: 'from-image',
        premultiplyAlpha: 'default',
        colorSpaceConversion: 'default'
      });
    }
    const objectUrl = URL.createObjectURL(blob);
    const image = new Image();
    let loaded = false;
    try {
      const decoded = await new Promise((resolve, reject) => {
        image.onload = () => resolve(image);
        image.onerror = () => reject(new Error('Unable to decode the image.'));
        image.src = objectUrl;
      });
      loaded = true;
      return decoded;
    } finally {
      image.onload = null;
      image.onerror = null;
      if (!loaded) image.src = '';
      URL.revokeObjectURL(objectUrl);
    }
  };

  const releaseDecodedImage = (image) => {
    if (!image) return;
    if (typeof image.close === 'function') {
      image.close();
      return;
    }
    if (image instanceof HTMLImageElement) {
      image.onload = null;
      image.onerror = null;
      image.src = '';
    }
  };

  const createCheckerboard = (context, width, height, dpr) => {
    const scale = dpr >= 2 ? 1 : 1;
    const tile = 16 * scale;
    context.save();
    context.fillStyle = '#fff';
    context.fillRect(0, 0, width, height);
    context.fillStyle = '#f0f3f8';
    for (let y = 0; y < height; y += tile * 2) {
      for (let x = 0; x < width; x += tile * 2) {
        context.fillRect(x, y + tile, tile, tile);
        context.fillRect(x + tile, y, tile, tile);
      }
    }
    context.restore();
  };

  class DreamItImageEditor {
    constructor(root) {
      this.root = root;
      if (!this.root) return;

      this.canvas = root.querySelector('[data-editor-canvas]');
      this.heading = root.querySelector('[data-editor-heading]');
      this.alert = root.querySelector('[data-editor-alert]');
      this.emptySpaceGuidance = root.querySelector('[data-editor-empty-space-guidance]');
      this.qualityWarning = root.querySelector('[data-editor-quality-warning]');
      this.fillLimitGuidance = root.querySelector('[data-editor-fill-limit]');
      this.originalDimensions = root.querySelector('[data-editor-original-dimensions]');
      this.outputDimensions = root.querySelector('[data-editor-output-dimensions]');
      this.scaleReadout = root.querySelector('[data-editor-scale]');
      this.zoomOutButton = root.querySelector('[data-editor-zoom-out]');
      this.zoomInButton = root.querySelector('[data-editor-zoom-in]');
      this.sizeSlider = root.querySelector('[data-editor-size]');
      this.fitButton = root.querySelector('[data-editor-fit]');
      this.fillButton = root.querySelector('[data-editor-fill]');
      this.centreButton = root.querySelector('[data-editor-centre]');
      this.rotateLeftButton = root.querySelector('[data-editor-rotate-left]');
      this.rotateRightButton = root.querySelector('[data-editor-rotate-right]');
      this.resetButton = root.querySelector('[data-editor-reset]');
      this.exportButton = root.querySelector('[data-editor-export]');
      this.downloadButton = root.querySelector('[data-editor-download]');
      this.chooseButton = root.querySelector('[data-editor-choose]');
      this.textRoot = root.querySelector('[data-dim-text-controls]');
      this.imageControlsPanel = root.querySelector('[data-dim-image-panel]');
      this.textControlsPanel = root.querySelector('[data-dim-text-panel]');

      this.productName = '';
      this.sourceRoute = '';
      this.initialPlacement = 'fit';
      this.outputPhysicalSizeMm = null;
      this.ppi = null;
      this.sourceBlob = null;
      this.composition = undefined;
      this.bitmap = null;
      this.outputWidth = 0;
      this.outputHeight = 0;
      this.viewScaleX = 1;
      this.viewScaleY = 1;
      this.rafId = 0;
      this.rendering = false;
      this.destroyed = false;
      this.dragging = false;
      this.activePointer = null;
      this.pointers = new Map();
      this.pinch = null;
      this.gestureTask = null;
      this.dragTextLayerId = null;
      this.lastPointer = { x: 0, y: 0 };
      this.resizeObserver = null;
      this.dirty = false;
      this.revision = 0;
      this.loadRequest = 0;
      this.exportInFlight = false;
      this.downloadInFlight = false;
      this.replacementPending = false;
      this.preparationQueue = Promise.resolve();
      this.preparationOwners = 0;
      this.preparationActive = false;
      this.preparedCandidate = null;
      this.dirtyEventRaf = 0;
      this.qualityCautionVisible = null;
      this.emptyGuidanceVisible = null;
      this.fillLimitVisible = false;
      this.messages = {
        heading: root.dataset.messageHeading || 'Your Dream is below',
        help: root.dataset.messageHelp || 'Move and resize your image inside the artwork area. Transparent areas will not be printed.',
        dirty: root.dataset.messageDirty || 'Your image has changed. Select Continue to prepare the updated version.',
        ready: root.dataset.messageReady || 'Your prepared image is ready.',
        positioning: root.dataset.messagePositioning || 'Position your image, then prepare it',
        emptySpace: root.dataset.messageEmptySpace || 'Empty space is normal. You can enlarge and reposition your image here, then check it on the product preview before approving it.',
        qualityWarning: root.dataset.messageQualitySummary || 'Your image may look soft when printed at this size. Reduce its size for the sharpest result.',
        fillLimit: root.dataset.messageFillLimit || 'This image is too small to fill the complete artwork area within the enlargement limit. Some transparent space will remain. You can continue, reposition it, or choose a larger image.'
      };

      this.baseWidth = 0;
      this.baseHeight = 0;
      this.createBaseline = null;
      this.state = {
        x: 0,
        y: 0,
        scale: 1,
        rotation: 0
      };
      this.lastState = null;

      this.textLayers = Object.freeze([]);
      this.textLayouts = Object.freeze([]);
      this.textControls = null;
      this.textFontLoader = null;
      this.textPendingInvalid = false;
      this.textLayoutError = null;
      this.textLayoutRequest = 0;
      this.exportAdmission = null;
      this.textPlacementConstraints = null;
      this.textPlacementRevision = 0;
      this.draggingKind = 'image';
      this.textRuntimeEnabled = window.DIMRuntime?.features?.text === true
        || window.DIMRuntime?.features?.textEnabled === true;

      this.updateTextControlsVisibility();

      this.ready = false;
      this.minScale = 0.01;

      if (!this.canvas) return;

      this.context = this.canvas.getContext('2d');
      this.context.imageSmoothingEnabled = true;
      this.context.imageSmoothingQuality = 'high';

      this.initialiseText();
      this.bindEvents();
      this.bindKeyboard();
      this.enableControls(false);
      root.dataset.editorState = 'empty';
    }

    bindEvents() {
      this.onWindowResize = () => {
        if (!this.ready) return;
        this.updatePresentationSize();
        this.scheduleRender();
      };
      this.onPresentationFrameChange = () => {
        if (!this.ready) return;
        this.updatePresentationSize();
        this.scheduleRender();
      };
      window.addEventListener('resize', this.onWindowResize, { passive: true });
      this.onDownloadPageExit = () => { this.cancelPointer(); this.releaseDownloadUrl(); };
      window.addEventListener('pagehide', this.onDownloadPageExit);
      this.root.addEventListener('dreamit:editor-presentation-frame-change', this.onPresentationFrameChange);
      this.onInteractionOwnerChange = () => this.cancelPointer();
      this.root.addEventListener('dreamit:editor-task-change', this.onInteractionOwnerChange);
      this.root.addEventListener('dreamit:editor-surface-change', this.onInteractionOwnerChange);
      this.onNudgeControl = (event) => this.nudge(event.detail?.key, false, true);
      this.root.addEventListener('dreamit:editor-nudge', this.onNudgeControl);
      if (this.resizeObserver) return;
      this.resizeObserver = new ResizeObserver(() => {
        if (this.ready) this.scheduleRender();
      });
      this.resizeObserver.observe(this.canvas);

      this.zoomOutButton?.addEventListener('click', () => this.zoomBy(-0.05));
      this.zoomInButton?.addEventListener('click', () => this.zoomBy(0.05));
      this.sizeSlider?.addEventListener('input', () => this.onSliderInput());
      this.fitButton?.addEventListener('click', () => this.fit());
      this.fillButton?.addEventListener('click', () => this.fill());
      this.centreButton?.addEventListener('click', () => this.centre());
      this.rotateLeftButton?.addEventListener('click', () => this.rotate(-1));
      this.rotateRightButton?.addEventListener('click', () => this.rotate(1));
      this.resetButton?.addEventListener('click', () => this.reset());
      this.chooseButton?.addEventListener('click', () => {
        emit(this.root, 'dreamit:editor-choose');
      });
      this.downloadButton?.addEventListener('click', () => {
        this.requestDownload().catch((error) => {
          emit(this.root, 'dreamit:editor-error', { message: error.message || 'We couldn’t download your image.' });
        });
      });
      this.exportButton?.addEventListener('click', () => {
        this.requestExport().catch((error) => {
          emit(this.root, 'dreamit:editor-error', { message: error.message || 'We couldn’t prepare your image.' });
        });
      });

      this.canvas?.addEventListener('pointerdown', (event) => this.onPointerDown(event), { passive: false });
      this.canvas?.addEventListener('pointermove', (event) => this.onPointerMove(event));
      this.canvas?.addEventListener('pointerup', (event) => this.onPointerEnd(event));
      this.canvas?.addEventListener('pointercancel', (event) => { if (this.pointers.has(event.pointerId)) this.cancelPointer(); });
      this.canvas?.addEventListener('lostpointercapture', (event) => { if (this.pointers.has(event.pointerId)) this.cancelPointer(); });
      if (this.canvas) this.canvas.tabIndex = 0;
      this.canvas?.addEventListener('focus', () => this.scheduleRender());
    }

    bindKeyboard() {
      this.root.addEventListener('keydown', (event) => {
        if (event.target !== this.canvas || !this.ready || this.isInteractionLocked()) return;
        const key = event.key;
        if (key === 'Escape') {
          this.cancelPointer();
          return;
        }
        const task = this.directManipulationTask();
        if (task === 'locked') return;
        if (key === '+' || key === '=') {
          if (task === 'words') return;
          event.preventDefault();
          this.zoomBy(0.05);
          return;
        }
        if (key === '-') {
          if (task === 'words') return;
          event.preventDefault();
          this.zoomBy(-0.05);
          return;
        }
        if (key.startsWith('Arrow')) {
          event.preventDefault();
          this.nudge(key, event.shiftKey);
          return;
        }
      });
    }

    get effectiveState() {
      const effective = effectiveDimensions(this.baseWidth, this.baseHeight, this.state.rotation);
      return {
        width: effective.width,
        height: effective.height,
        scale: this.state.scale,
        x: this.state.x,
        y: this.state.y,
        rotation: this.state.rotation
      };
    }

    setProductName(value) {
      this.productName = value || '';
    }

    initialiseText() {
      const model = window.DIMTextModel;
      const controlsApi = window.DIMTextControls;
      const fontsApi = window.DIMTextFonts;
      const runtimeManifest = window.DIMRuntime?.fontCatalogue;
      let approvedManifest = null;
      if (fontsApi?.validateApprovedManifest && runtimeManifest) {
        try {
          approvedManifest = fontsApi.validateApprovedManifest(runtimeManifest);
          const baseUrl = document.baseURI || window.location?.href;
          const allowedOrigins = [...new Set(Object.values(approvedManifest.fonts).map((entry) => {
            try { return new URL(entry.url, baseUrl).origin; } catch (_) { return null; }
          }).filter(Boolean))];
          this.textFontLoader = fontsApi.createFontLoader(runtimeManifest, { baseUrl, allowedOrigins });
        } catch (error) {
          this.textLayoutError = error;
          this.textFontLoader = null;
        }
      }
      if (!this.textRoot || !model || !controlsApi?.createTextControls) return;
      this.textControls = controlsApi.createTextControls(this.textRoot, {
        initialLayers: this.textLayers,
        fontManifest: this.textRuntimeEnabled ? approvedManifest : null,
        ensureFont: this.textFontLoader ? (id, revision, options) => this.textFontLoader.ensure(id, revision, options) : null,
        onChange: (change) => {
          this.textPendingInvalid = change.pendingInvalid === true;
          if (!this.textPendingInvalid) this.textLayers = change.layers;
          this.updateTextControlsVisibility();
          this.updateEditingMode(this.textControls?.getSelection?.());
          if (this.ready) {
            this.markDirty(true);
            this.enableControls(true);
            this.refreshTextLayouts();
          } else this.revision += 1;
        },
        onSelectionChange: (selection) => {
          this.updateEditingMode(selection);
          this.scheduleRender();
        }
      });
      this.updateEditingMode(this.textControls.getSelection?.());
    }

    updateEditingMode(selection = null) {
      const textSelected = selection?.type === 'text';
      if (this.imageControlsPanel) this.imageControlsPanel.hidden = textSelected || Boolean(this.composition);
      if (this.textControlsPanel) this.textControlsPanel.hidden = !textSelected;
    }

    updateTextControlsVisibility() {
      if (!this.textRoot) return;
      this.textRoot.hidden = !this.textRuntimeEnabled && this.textLayers.length === 0;
    }

    configureText(layers = [], { dirty = false, preserveHistory = false, reason = 'text-restore' } = {}) {
      const model = window.DIMTextModel;
      let immutable;
      if (model?.validateLayers) {
        immutable = Object.freeze(model.validateLayers(layers, { allowLegacyMultilineCurve: true }).map((layer) => Object.freeze({ ...layer })));
      } else {
        if (!Array.isArray(layers) || layers.length > 3) throw new Error('Text support is unavailable for this saved design.');
        immutable = Object.freeze(layers.map((layer) => Object.freeze({ ...layer })));
      }
      this.textLayers = immutable;
      this.updateTextControlsVisibility();
      this.textPendingInvalid = false;
      this.textLayoutError = null;
      this.textLayouts = Object.freeze([]);
      this.textControls?.setInkColours?.(this.composition?.capability.inkColours || null);
      this.textControls?.setLayers?.(immutable, { dirty: false, preserveHistory, reason });
      if (dirty && this.ready) this.markDirty();
      this.refreshTextLayouts();
      if (this.ready && this.composition) this.enableControls(!this.destroyed);
      return this.getTextLayers();
    }

    setExportAdmission(callback = null) {
      if (callback !== null && typeof callback !== 'function') throw new TypeError('Export admission must be a function.');
      this.exportAdmission = callback;
      if (callback && this.ready && this.textLayers.length) this.refreshTextLayouts();
    }

    setTextRecoveryAdmission(callback = null) {
      this.setExportAdmission(callback);
    }

    setTextPlacementConstraints(constraints = null) {
      if (constraints === null) {
        this.textPlacementConstraints = null;
        this.textPlacementRevision += 1;
        this.textControls?.setPlacementGuidance?.(null);
        return null;
      }
      const exactKeys = (value, keys) => value && typeof value === 'object' && !Array.isArray(value)
        && Object.keys(value).sort().join(',') === [...keys].sort().join(',');
      if (!exactKeys(constraints, ['schemaVersion', 'productId', 'outputWidth', 'outputHeight', 'status', 'rectPx'])
        || constraints.schemaVersion !== 'text-placement@1'
        || typeof constraints.productId !== 'string' || !constraints.productId
        || constraints.productId !== this.productId
        || !Number.isSafeInteger(constraints.outputWidth) || !Number.isSafeInteger(constraints.outputHeight)
        || constraints.outputWidth !== this.outputWidth || constraints.outputHeight !== this.outputHeight
        || !['verified-rectangle', 'advisory'].includes(constraints.status)) {
        throw new Error('Lettering guidance does not match this product.');
      }
      let rectPx = null;
      if (constraints.status === 'advisory') {
        if (constraints.rectPx !== null) throw new Error('Lettering guidance does not match this product.');
      } else {
        const rect = constraints.rectPx;
        if (!exactKeys(rect, ['x', 'y', 'width', 'height'])
          || ![rect.x, rect.y, rect.width, rect.height].every(Number.isFinite)
          || rect.x < 0 || rect.y < 0 || rect.width <= 0 || rect.height <= 0
          || rect.x + rect.width > this.outputWidth || rect.y + rect.height > this.outputHeight) {
          throw new Error('The lettering safe area is not available for this product.');
        }
        rectPx = Object.freeze({ x: rect.x, y: rect.y, width: rect.width, height: rect.height });
      }
      const accepted = Object.freeze({
        schemaVersion: 'text-placement@1',
        productId: constraints.productId,
        outputWidth: constraints.outputWidth,
        outputHeight: constraints.outputHeight,
        status: constraints.status,
        rectPx
      });
      this.textPlacementConstraints = accepted;
      this.textPlacementRevision += 1;
      this.textControls?.setPlacementGuidance?.(accepted.status);
      return accepted;
    }

    async verifyExportAdmission(snapshot) {
      const runtime = window.DIMRuntime;
      if (!runtime?.enabled) return this.textPlacementConstraints;
      if (typeof runtime.verify !== 'function') throw new Error('The protected export runtime is unavailable.');
      if (typeof this.exportAdmission !== 'function') {
        throw new Error('The current product selection must be verified before exporting.');
      }
      const placementRevision = this.textPlacementRevision;
      const context = await runtime.verify({ force: true });
      if (!this.exportSnapshotCurrent(snapshot)) throw new Error('The design changed while export access was being verified.');
      const admitted = await this.exportAdmission(Object.freeze({
        snapshot,
        productId: this.productId,
        sourceRoute: this.sourceRoute,
        context
      }));
      if (admitted !== true) throw new Error('The current product selection did not admit this export.');
      if (!this.exportSnapshotCurrent(snapshot)) throw new Error('The design changed while export access was being admitted.');
      const hasText = Array.isArray(snapshot.editorState?.textLayers) && snapshot.editorState.textLayers.length > 0;
      if (hasText && (this.textPlacementRevision === placementRevision || !this.textPlacementConstraints)) {
        throw new Error('Current lettering guidance is needed before continuing.');
      }
      return this.textPlacementConstraints;
    }

    withTextState(imageState, sourceState = null) {
      const model = window.DIMTextModel;
      const sourceLayers = Array.isArray(sourceState?.textLayers) ? sourceState.textLayers : [];
      const active = this.textRuntimeEnabled || Boolean(this.textRoot) || sourceLayers.length > 0 || this.textLayers.length > 0;
      if (!active) return Object.freeze({ ...imageState });
      if (model?.freezeEditorState) {
        return model.freezeEditorState({
          ...imageState,
          textSchemaVersion: sourceState?.textSchemaVersion ?? model.SCHEMA_VERSION,
          textRendererVersion: sourceState?.textRendererVersion ?? model.RENDERER_VERSION,
          textLayers: sourceLayers
        });
      }
      if (sourceLayers.length) {
        return Object.freeze({
          ...imageState,
          textSchemaVersion: sourceState?.textSchemaVersion ?? 1,
          textRendererVersion: sourceState?.textRendererVersion ?? 1,
          textLayers: Object.freeze(sourceLayers.map((layer) => Object.freeze({ ...layer })))
        });
      }
      return Object.freeze({ ...imageState });
    }

    getTextLayers() {
      return Object.freeze(this.textLayers.map((layer) => Object.freeze({ ...layer })));
    }

    async refreshTextLayouts() {
      const request = ++this.textLayoutRequest;
      if (!this.ready || !this.textLayers.length) {
        this.textLayouts = Object.freeze([]);
        this.textLayoutError = null;
        this.textControls?.setLayoutWarning?.(null);
        this.scheduleRender();
        return this.textLayouts;
      }
      const snapshot = this.captureExportSnapshot();
      try {
        let placementWarning = null;
        const layouts = await this.ensureTextReady(snapshot, {
          allowClippedPreview: true, onPlacementWarning: (error) => { placementWarning = error; }
        });
        if (request !== this.textLayoutRequest || !this.exportSnapshotCurrent(snapshot)) return null;
        this.textLayouts = layouts;
        this.textLayoutError = placementWarning;
        this.textControls?.setLayoutWarning?.(this.textLayoutError);
        this.scheduleRender();
        return layouts;
      } catch (error) {
        if (request !== this.textLayoutRequest) return null;
        this.textLayouts = Object.freeze([]);
        this.textLayoutError = error;
        this.textControls?.setLayoutWarning?.(error);
        this.scheduleRender();
        return null;
      }
    }

    async ensureTextReady(snapshot, { admissionVerified = false, placementConstraints = this.textPlacementConstraints, allowClippedPreview = false, onPlacementWarning = null } = {}) {
      const model = window.DIMTextModel;
      const renderer = window.DIMTextRenderer;
      if (!snapshot?.editorState) throw new Error('The prepared editor state is unavailable.');
      if (!model?.textState) {
        if (snapshot.composition) throw new Error('Text support is unavailable for this Words-only composition.');
        if (Array.isArray(snapshot.editorState.textLayers) && snapshot.editorState.textLayers.length) {
          throw new Error('Text support is unavailable. Your saved text has been retained.');
        }
        return Object.freeze([]);
      }
      const layers = Object.freeze(model.validateLayers(model.textState(snapshot.editorState), { forExport: true })
        .map((layer) => Object.freeze({ ...layer })));
      if (snapshot.composition) {
        const composition = window.DIMSceneIdentity.validateComposition(snapshot.composition);
        window.DIMSceneIdentity.validateCompositionText(composition, layers, { forExport: true });
      }
      if (!layers.length) return Object.freeze([]);
      if (this.textPendingInvalid) throw new Error('Correct the text field shown before continuing.');
      let activePlacement = placementConstraints;
      if (!this.textRuntimeEnabled && !admissionVerified) activePlacement = await this.verifyExportAdmission(snapshot);
      if (!this.textFontLoader || !renderer?.layoutLayers || !renderer?.isInside || !this.context) {
        throw this.textLayoutError || new Error('Approved text fonts are unavailable. Your saved text has been retained.');
      }
      await Promise.all(layers.map((layer) => this.textFontLoader.ensure(layer.fontId, layer.fontRevision, { text: layer.text })));
      if (!this.exportSnapshotCurrent(snapshot)) throw new Error('The design changed while its text was being prepared.');
      const layouts = renderer.layoutLayers(
        this.context, layers, snapshot.outputWidth, snapshot.outputHeight, this.textFontLoader.rendererCatalog
      );
      const placementError = this.textPlacementError(layouts, snapshot, activePlacement);
      if (placementError && !allowClippedPreview) throw placementError;
      if (allowClippedPreview) onPlacementWarning?.(placementError);
      return layouts;
    }

    async ensurePreparedTextReady(candidate, { placementConstraints = null } = {}) {
      const requireCurrent = () => {
        if (!candidate || candidate.owner !== this || candidate.status !== 'prepared'
          || this.preparedCandidate !== candidate || candidate.editorRevision !== this.revision || this.destroyed) {
          throw new Error('The prepared design changed while its lettering was being checked.');
        }
        if (candidate.composition) window.DIMSceneIdentity.validateComposition(candidate.composition);
      };
      requireCurrent();
      const layers = window.DIMTextModel.validateLayers(window.DIMTextModel.textState(candidate.editorState), { forExport: true });
      if (candidate.composition) window.DIMSceneIdentity.validateCompositionText(candidate.composition, layers, { forExport: true });
      if (!layers.length) return Object.freeze([]);
      if (!this.textFontLoader || !window.DIMTextRenderer?.layoutLayers || !this.context) throw new Error('Approved text fonts are unavailable.');
      await Promise.all(layers.map(layer => this.textFontLoader.ensure(layer.fontId, layer.fontRevision, { text: layer.text })));
      requireCurrent();
      const layouts = window.DIMTextRenderer.layoutLayers(this.context, layers, candidate.outputWidth, candidate.outputHeight, this.textFontLoader.rendererCatalog);
      const placementError = this.textPlacementError(layouts, candidate, placementConstraints);
      if (placementError) throw placementError;
      return layouts;
    }

    textPlacementError(layouts, snapshot, placementConstraints) {
      const renderer = window.DIMTextRenderer;
      const output = { left: 0, top: 0, right: snapshot.outputWidth, bottom: snapshot.outputHeight };
      if (layouts.some((layout) => !renderer.isInside(layout.bounds, output, 0))) {
        return new Error('Keep all text inside the printable artwork area before continuing.');
      }
      if (placementConstraints?.status === 'verified-rectangle') {
        const rect = placementConstraints.rectPx;
        const safeArea = { left: rect.x, top: rect.y, right: rect.x + rect.width, bottom: rect.y + rect.height };
        if (layouts.some((layout) => !renderer.isInside(layout.bounds, safeArea, 0))) {
          return new Error('Move all lettering into the safe area before continuing.');
        }
      }
      return null;
    }

    exportSnapshotCurrent(snapshot) {
      if (!snapshot || !this.ready || this.destroyed || snapshot.revision !== this.revision
        || snapshot.productId !== this.productId || snapshot.outputWidth !== this.outputWidth
        || snapshot.outputHeight !== this.outputHeight || snapshot.sourceBlob !== this.sourceBlob
        || snapshot.composition !== this.composition
        || snapshot.bitmap !== this.bitmap || snapshot.baseWidth !== this.baseWidth || snapshot.baseHeight !== this.baseHeight) return false;
      const current = this.getEditorState();
      if (window.DIMTextModel?.editorStatesEqual) {
        return window.DIMTextModel.editorStatesEqual(snapshot.editorState, current);
      }
      return JSON.stringify(snapshot.editorState) === JSON.stringify(current);
    }

    renderText(context, layouts, { display = false } = {}) {
      if (!layouts?.length) return;
      const renderer = window.DIMTextRenderer;
      if (!renderer?.drawLayouts) throw new Error('Your browser cannot prepare lettering.');
      renderer.drawLayouts(context, layouts);
      if (!display || !this.textControls) return;
      const selection = this.textControls.getSelection?.();
      const selected = selection?.type === 'text' ? layouts.find((layout) => layout.layer.id === selection.id) : null;
      if (!selected) return;
      context.save();
      try {
        context.strokeStyle = 'rgba(0, 86, 145, 0.9)';
        context.lineWidth = 1 / Math.max(this.viewScaleX || 1, this.viewScaleY || 1);
        context.setLineDash?.([4 / Math.max(this.viewScaleX || 1, 0.0001), 3 / Math.max(this.viewScaleX || 1, 0.0001)]);
        context.strokeRect(
          selected.bounds.left, selected.bounds.top,
          selected.bounds.right - selected.bounds.left, selected.bounds.bottom - selected.bounds.top
        );
      } finally {
        context.restore();
      }
    }

    async load(args) {
      const request = ++this.loadRequest;
      const candidate = await this.prepareLoad(args);
      if (request !== this.loadRequest || candidate.editorRevision !== this.revision) {
        this.discardPreparedLoad(candidate);
        return null;
      }
      try {
        this.commitPreparedLoad(candidate);
      } catch (error) {
        this.discardPreparedLoad(candidate);
        throw error;
      }
    }

    async prepareLoad({ blob, composition, resolution, productId, outputWidth, outputHeight, restoredState = null, productName = '', sourceRoute = '', outputPhysicalSizeMm = null, ppi = null, initialPlacement = 'fit', createBaseline = null, inspection = null }) {
      this.cancelPointer();
      const editorRevision = this.revision;
      const preparedOutputWidth = asInt(outputWidth);
      const preparedOutputHeight = asInt(outputHeight);
      if (!preparedOutputWidth || !preparedOutputHeight) {
        throw new Error('This product output size is not configured correctly.');
      }
      if (this.exportInFlight || this.downloadInFlight) {
        throw new Error('Wait for the current image export to finish, then choose the replacement again.');
      }
      this.preparationOwners += 1;
      const previousPreparation = this.preparationQueue;
      let releaseQueue;
      const preparationLease = new Promise((resolve) => { releaseQueue = resolve; });
      this.preparationQueue = previousPreparation.catch(() => {}).then(() => preparationLease);
      let released = false;
      let preparedCandidate = null;
      const releasePreparation = () => {
        if (released) return;
        released = true;
        if (this.preparedCandidate === preparedCandidate) this.preparedCandidate = null;
        this.preparationActive = false;
        this.preparationOwners = Math.max(0, this.preparationOwners - 1);
        releaseQueue();
      };
      await previousPreparation.catch(() => {});
      if (editorRevision !== this.revision || this.destroyed) {
        releasePreparation();
        throw new Error('The image changed while replacement was being prepared. Select the replacement again.');
      }
      this.preparationActive = true;
      let bitmap;
      try {
        if (composition !== undefined) {
          if (blob !== null || !window.DIMSceneIdentity?.validateComposition) throw new Error('Words-only composition must have no original image.');
          const accepted = window.DIMSceneIdentity.validateComposition(composition, { resolution, originalImageKey: '', originalRevision: 0 });
          const editorState = this.withTextState({ version: 1, outputWidth: preparedOutputWidth, outputHeight: preparedOutputHeight,
            x: 0, y: 0, scale: 1, rotation: 0 }, restoredState || (this.ready ? this.getEditorState() : null));
          preparedCandidate = { owner: this, status: 'prepared', releasePreparation, composition: accepted,
            bitmap: null, sourceBlob: null, productId: String(productId || ''), outputWidth: preparedOutputWidth,
            outputHeight: preparedOutputHeight, sourceRoute: String(sourceRoute || ''), initialPlacement: 'fit',
            outputPhysicalSizeMm: outputPhysicalSizeMm && typeof outputPhysicalSizeMm === 'object' ? { width: Number(outputPhysicalSizeMm.width), height: Number(outputPhysicalSizeMm.height) } : null,
            ppi: Number.isFinite(Number(ppi)) && Number(ppi) > 0 ? Number(ppi) : null, productName: productName || '',
            baseWidth: 0, baseHeight: 0, minScale: 1, editorState, createBaseline: null, restored: Boolean(restoredState), editorRevision };
          this.preparedCandidate = preparedCandidate;
          return preparedCandidate;
        }
        const verifiedInspection = inspection?.blob === blob
          ? inspection
          : await imageInspector.inspect(blob, { expectedMime: blob?.type || '' });
        imageInspector.admit(verifiedInspection, {
          outputWidth: preparedOutputWidth,
          outputHeight: preparedOutputHeight,
          retainedWidth: this.ready ? this.baseWidth : 0,
          retainedHeight: this.ready ? this.baseHeight : 0,
          retainedEncodedBytes: this.ready ? Number(this.sourceBlob?.size) || 0 : 0
        });
        bitmap = await decodeImageSource(blob);
        const baseWidth = bitmap instanceof HTMLImageElement ? asInt(bitmap.naturalWidth) : asInt(bitmap.width);
        const baseHeight = bitmap instanceof HTMLImageElement ? asInt(bitmap.naturalHeight) : asInt(bitmap.height);
        const exactOrientedDimensions = baseWidth === verifiedInspection.width && baseHeight === verifiedInspection.height;
        if (!baseWidth || !baseHeight || !exactOrientedDimensions) {
          throw new Error('This image could not be decoded.');
        }

        // Text-only product handoffs retain lettering, while image placement is
        // recalculated for the target output. Older dimensionless image states
        // remain supported when they contain an actual saved scale.
        const restoredPlacement = restoredState && typeof restoredState === 'object'
          && Number.isFinite(Number(restoredState.scale)) && Number(restoredState.scale) > 0
          && (restoredState.outputWidth == null || Number(restoredState.outputWidth) === preparedOutputWidth)
          && (restoredState.outputHeight == null || Number(restoredState.outputHeight) === preparedOutputHeight)
          ? restoredState : null;
        const rotation = restoredPlacement ? quarterTurns(restoredPlacement.rotation) : 0;
        const effective = effectiveDimensions(baseWidth, baseHeight, rotation);
        const fitScale = clamp(Math.min(preparedOutputWidth / effective.width, preparedOutputHeight / effective.height), 0.01, MAX_SCALE);
        const minimum = minScale(fitScale);
        const placement = initialPlacement === 'fill' ? 'fill' : 'fit';
        const fillScale = Math.max(preparedOutputWidth / effective.width, preparedOutputHeight / effective.height);
        const scale = restoredPlacement
          ? clamp(toNumber(restoredPlacement.scale), minimum, MAX_SCALE)
          : (placement === 'fill' ? clamp(fillScale, minimum, MAX_SCALE) : fitScale);
        const renderedWidth = effective.width * scale;
        const renderedHeight = effective.height * scale;
        const imageEditorState = {
          version: 1,
          outputWidth: preparedOutputWidth,
          outputHeight: preparedOutputHeight,
          x: restoredPlacement
            ? clampPosition(toNumber(restoredPlacement.x), renderedWidth, preparedOutputWidth)
            : (preparedOutputWidth - renderedWidth) / 2,
          y: restoredPlacement
            ? clampPosition(toNumber(restoredPlacement.y), renderedHeight, preparedOutputHeight)
            : (preparedOutputHeight - renderedHeight) / 2,
          scale,
          rotation
        };
        const hasRestoredText = restoredState && typeof restoredState === 'object'
          && (Object.hasOwn(restoredState, 'textLayers') || Object.hasOwn(restoredState, 'textSchemaVersion'));
        const textSourceState = hasRestoredText ? restoredState : (!restoredState && this.ready ? this.getEditorState() : null);
        const editorState = this.withTextState(imageEditorState, textSourceState);
        const governedEffective = effectiveDimensions(baseWidth, baseHeight, 0);
        const governedFitScale = clamp(Math.min(
          preparedOutputWidth / governedEffective.width,
          preparedOutputHeight / governedEffective.height
        ), 0.01, MAX_SCALE);
        const governedFillScale = Math.max(
          preparedOutputWidth / governedEffective.width,
          preparedOutputHeight / governedEffective.height
        );
        const governedScale = placement === 'fill'
          ? clamp(governedFillScale, minScale(governedFitScale), MAX_SCALE)
          : governedFitScale;
        const governedState = {
          version: 1,
          outputWidth: preparedOutputWidth,
          outputHeight: preparedOutputHeight,
          x: (preparedOutputWidth - governedEffective.width * governedScale) / 2,
          y: (preparedOutputHeight - governedEffective.height * governedScale) / 2,
          scale: governedScale,
          rotation: 0
        };
        const expectedSourceWidth = asInt(createBaseline?.sourceSizePx?.width);
        const expectedSourceHeight = asInt(createBaseline?.sourceSizePx?.height);
        const expectedOutputWidth = asInt(createBaseline?.outputSizePx?.width);
        const expectedOutputHeight = asInt(createBaseline?.outputSizePx?.height);
        const expectedPlacement = createBaseline?.initialPlacement === 'fill' ? 'fill'
          : (createBaseline?.initialPlacement === 'fit' ? 'fit' : '');
        const verifiedCreateBaseline = sourceRoute === 'create'
          && expectedSourceWidth === baseWidth
          && expectedSourceHeight === baseHeight
          && expectedOutputWidth === preparedOutputWidth
          && expectedOutputHeight === preparedOutputHeight
          && expectedPlacement === placement
          ? {
              sourceSizePx: { width: expectedSourceWidth, height: expectedSourceHeight },
              outputSizePx: { width: expectedOutputWidth, height: expectedOutputHeight },
              initialPlacement: expectedPlacement,
              editorState: governedState
            }
          : null;
        preparedCandidate = {
          owner: this,
          status: 'prepared',
          releasePreparation,
          bitmap,
          sourceBlob: blob,
          productId: String(productId || ''),
          outputWidth: preparedOutputWidth,
          outputHeight: preparedOutputHeight,
          sourceRoute: String(sourceRoute || ''),
          initialPlacement: placement,
          outputPhysicalSizeMm: outputPhysicalSizeMm && typeof outputPhysicalSizeMm === 'object'
            ? { width: Number(outputPhysicalSizeMm.width), height: Number(outputPhysicalSizeMm.height) }
            : null,
          ppi: Number.isFinite(Number(ppi)) && Number(ppi) > 0 ? Number(ppi) : null,
          productName: productName || '',
          baseWidth,
          baseHeight,
          minScale: minimum,
          editorState,
          createBaseline: verifiedCreateBaseline,
          restored: Boolean(restoredState),
          editorRevision
        };
        this.preparedCandidate = preparedCandidate;
        return preparedCandidate;
      } catch (error) {
        releaseDecodedImage(bitmap);
        releasePreparation();
        throw error;
      }
    }

    commitPreparedLoad(candidate) {
      if (!candidate || candidate.owner !== this || candidate.status !== 'prepared' || (!candidate.bitmap && !candidate.composition)) {
        throw new Error('This prepared image is no longer available.');
      }
      if (candidate.editorRevision !== this.revision) {
        throw new Error('The image changed while replacement was being prepared. Select the replacement again.');
      }
      if (candidate.composition) window.DIMSceneIdentity.validateComposition(candidate.composition);
      const previous = {
        revision: this.revision,
        productName: this.productName,
        productId: this.productId,
        outputWidth: this.outputWidth,
        outputHeight: this.outputHeight,
        sourceRoute: this.sourceRoute,
        outputPhysicalSizeMm: this.outputPhysicalSizeMm,
        ppi: this.ppi,
        initialPlacement: this.initialPlacement,
        sourceBlob: this.sourceBlob,
        composition: this.composition,
        bitmap: this.bitmap,
        baseWidth: this.baseWidth,
        baseHeight: this.baseHeight,
        createBaseline: this.createBaseline,
        minScale: this.minScale,
        state: { ...this.state },
        textLayers: this.getTextLayers(),
        textPendingInvalid: this.textPendingInvalid,
        textLayoutError: this.textLayoutError,
        textLayouts: this.textLayouts,
        textPlacementConstraints: this.textPlacementConstraints,
        textPlacementRevision: this.textPlacementRevision,
        ready: this.ready,
        dirty: this.dirty,
        destroyed: this.destroyed,
        emptyGuidanceVisible: this.emptyGuidanceVisible,
        fillLimitVisible: this.fillLimitVisible,
        rootState: this.root.dataset.editorState,
        canvasAspectRatio: this.canvas?.style.aspectRatio,
        fillLimitHidden: this.fillLimitGuidance?.hidden
      };
      try {
        this.revision += 1;
        this.destroyed = false;
        this.productId = candidate.productId;
        this.outputWidth = candidate.outputWidth;
        this.outputHeight = candidate.outputHeight;
        this.setTextPlacementConstraints(null);
        this.sourceRoute = candidate.sourceRoute;
        this.outputPhysicalSizeMm = candidate.outputPhysicalSizeMm;
        this.ppi = candidate.ppi;
        this.initialPlacement = candidate.initialPlacement;
        this.sourceBlob = candidate.sourceBlob;
        this.composition = candidate.composition;
        this.bitmap = candidate.bitmap;
        this.baseWidth = candidate.baseWidth;
        this.baseHeight = candidate.baseHeight;
        this.createBaseline = candidate.createBaseline;
        this.minScale = candidate.minScale;
        this.state = {
          x: candidate.editorState.x,
          y: candidate.editorState.y,
          scale: candidate.editorState.scale,
          rotation: candidate.editorState.rotation
        };
        this.setProductName(candidate.productName);
        this.updatePresentationSize();
        if (this.canvas) this.canvas.style.aspectRatio = `${this.outputWidth} / ${this.outputHeight}`;
        this.ready = true;
        this.configureText(candidate.editorState.textLayers || [], { dirty: false, preserveHistory: false });
        this.dirty = false;
        this.emptyGuidanceVisible = null;
        this.setFillLimit(this.initialPlacement === 'fill' && this.getFillScale() > MAX_SCALE);
        this.root.dataset.editorState = 'loaded';
        this.applyConstraints();
        this.updateMeta();
        this.scheduleRender();
        this.enableControls(true);
        this.updateControlsFromState();
        this.alert?.setAttribute('hidden', 'true');
        emit(this.root, 'dreamit:editor-ready', {
          productId: this.productId,
          outputWidth: this.outputWidth,
          outputHeight: this.outputHeight,
          sourceWidth: this.baseWidth,
          sourceHeight: this.baseHeight,
          restored: candidate.restored,
          editorState: this.getEditorState()
        });
      } catch (error) {
        this.revision = previous.revision;
        this.productName = previous.productName;
        this.productId = previous.productId;
        this.outputWidth = previous.outputWidth;
        this.outputHeight = previous.outputHeight;
        this.sourceRoute = previous.sourceRoute;
        this.outputPhysicalSizeMm = previous.outputPhysicalSizeMm;
        this.ppi = previous.ppi;
        this.initialPlacement = previous.initialPlacement;
        this.sourceBlob = previous.sourceBlob;
        this.composition = previous.composition;
        this.bitmap = previous.bitmap;
        this.baseWidth = previous.baseWidth;
        this.baseHeight = previous.baseHeight;
        this.createBaseline = previous.createBaseline;
        this.minScale = previous.minScale;
        this.state = previous.state;
        this.textLayers = previous.textLayers;
        this.textPendingInvalid = previous.textPendingInvalid;
        this.textLayoutError = previous.textLayoutError;
        this.textLayouts = previous.textLayouts;
        this.textPlacementConstraints = previous.textPlacementConstraints;
        this.textPlacementRevision = previous.textPlacementRevision;
        this.textControls?.setInkColours?.(previous.composition?.capability.inkColours || null);
        this.textControls?.setPlacementGuidance?.(previous.textPlacementConstraints?.status || null);
        this.textControls?.setLayers?.(previous.textLayers, { dirty: false, preserveHistory: false });
        this.updateTextControlsVisibility();
        this.ready = previous.ready;
        this.dirty = previous.dirty;
        this.destroyed = previous.destroyed;
        this.emptyGuidanceVisible = previous.emptyGuidanceVisible;
        this.fillLimitVisible = previous.fillLimitVisible;
        this.root.dataset.editorState = previous.rootState;
        if (this.canvas) this.canvas.style.aspectRatio = previous.canvasAspectRatio || '';
        if (this.fillLimitGuidance) this.fillLimitGuidance.hidden = previous.fillLimitHidden;
        throw error;
      }
      candidate.status = 'committed';
      this.releaseDownloadUrl();
      try {
        if (previous.bitmap !== this.bitmap) releaseDecodedImage(previous.bitmap);
      } catch (_) {}
      candidate.releasePreparation?.();
    }

    discardPreparedLoad(candidate) {
      if (!candidate || candidate.owner !== this || candidate.status !== 'prepared') return false;
      candidate.status = 'discarded';
      releaseDecodedImage(candidate.bitmap);
      candidate.releasePreparation?.();
      return true;
    }

    setReplacementPending(pending) {
      const next = Boolean(pending);
      if (this.replacementPending === next) return;
      this.replacementPending = next;
      if (next) {
        this.cancelPointer();
        this.root.dataset.editorReplacementPending = 'true';
      } else {
        delete this.root.dataset.editorReplacementPending;
      }
      this.enableControls(this.ready && !this.destroyed);
    }

    isInteractionLocked() {
      return this.replacementPending || this.preparationOwners > 0 || this.exportInFlight || this.downloadInFlight;
    }

    directManipulationTask() {
      // Shared legacy presentations retain their hit-based canvas contract.
      if (this.root.dataset.eEditorPresentation !== 'true') return 'legacy';
      const task = this.root.dataset.eEditorTask;
      return this.root.dataset.eEditorInteractive === 'true' && ['words', 'layout'].includes(task) ? task : 'locked';
    }

    nudge(key, shiftKey = false, fromControl = false) {
      if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(key)
        || !this.ready || this.isInteractionLocked()) return;
      let task = this.directManipulationTask();
      if (fromControl && this.root.dataset.eEditorPresentation === 'true') {
        // Position buttons are alternatives to direct canvas manipulation,
        // including the retained Words-only Layout controls in compact view.
        if (this.root.dataset.eEditorTask !== 'layout') return;
        task = this.composition ? 'words' : 'layout';
      }
      if (task === 'locked') return;
      const selection = this.textControls?.getSelection?.();
      if (task !== 'layout' && selection?.type === 'text') {
        const layer = this.textLayers.find(item => item.id === selection.id);
        if (layer) {
          const pixels = shiftKey ? 10 : 1;
          const horizontal = pixels / Math.max(1, this.outputWidth);
          const vertical = pixels / Math.max(1, this.outputHeight);
          const movement = { ArrowLeft: [-horizontal, 0], ArrowRight: [horizontal, 0],
            ArrowUp: [0, -vertical], ArrowDown: [0, vertical] }[key];
          this.textControls.applyPatch(layer.id, {
            x: clamp(layer.x + movement[0], -1, 2), y: clamp(layer.y + movement[1], -1, 2)
          }, { reason: 'text-nudge', coalesceKey: `${layer.id}:canvas-nudge` });
          return;
        }
      }
      if (task === 'words' || this.composition) return;
      const shorter = Math.min(this.outputWidth, this.outputHeight) || 1;
      const step = (shiftKey ? shorter * 0.05 : shorter * 0.01) || 1;
      if (key === 'ArrowLeft') this.setPosition(this.state.x - step, this.state.y, true);
      if (key === 'ArrowRight') this.setPosition(this.state.x + step, this.state.y, true);
      if (key === 'ArrowUp') this.setPosition(this.state.x, this.state.y - step, true);
      if (key === 'ArrowDown') this.setPosition(this.state.x, this.state.y + step, true);
    }

    validateRestoredState(restoredState) {
      if (!restoredState || typeof restoredState !== 'object') return null;
      if (this.composition) return { x: 0, y: 0, scale: 1, rotation: 0 };
      const rotation = quarterTurns(restoredState.rotation);
      const scale = clamp(toNumber(restoredState.scale), 0.0001, MAX_SCALE);
      const eff = effectiveDimensions(this.baseWidth, this.baseHeight, rotation);
      const fitScale = this.getFitScale(rotation);
      const minimum = minScale(fitScale);
      const boundedScale = clamp(scale, minimum, MAX_SCALE);
      const renderedWidth = eff.width * boundedScale;
      const renderedHeight = eff.height * boundedScale;
      return {
        rotation,
        scale: boundedScale,
        x: clampPosition(toNumber(restoredState.x), renderedWidth, this.outputWidth),
        y: clampPosition(toNumber(restoredState.y), renderedHeight, this.outputHeight)
      };
    }

    getEditorState() {
      return this.withTextState({
        version: 1,
        outputWidth: this.outputWidth,
        outputHeight: this.outputHeight,
        x: this.state.x,
        y: this.state.y,
        scale: this.state.scale,
        rotation: quarterTurns(this.state.rotation)
      }, { textLayers: this.textLayers });
    }

    fit(markAsDirty = true) {
      if (!this.ready || this.replacementPending || this.composition) return;
      const before = this.getEditorState();
      this.state.scale = this.getFitScale();
      this.state.rotation = quarterTurns(this.state.rotation);
      this.centreState();
      this.setFillLimit(false);
      this.completeTransform(before, markAsDirty);
      return this.getEditorState();
    }

    fill(markAsDirty = true) {
      if (!this.ready || this.replacementPending || this.composition) return;
      const before = this.getEditorState();
      const eff = effectiveDimensions(this.baseWidth, this.baseHeight, this.state.rotation);
      const fillScale = Math.max(this.outputWidth / eff.width, this.outputHeight / eff.height);
      this.state.scale = clamp(fillScale, this.minScale, MAX_SCALE);
      this.centreState();
      this.setFillLimit(fillScale > MAX_SCALE);
      this.completeTransform(before, markAsDirty);
      return this.getEditorState();
    }

    centre(markAsDirty = true) {
      if (!this.ready || this.replacementPending || this.composition) return;
      const before = this.getEditorState();
      this.centreState();
      this.completeTransform(before, markAsDirty);
      return this.getEditorState();
    }

    centreState() {
      if (this.composition) { this.state = { x: 0, y: 0, scale: 1, rotation: 0 }; return; }
      const eff = effectiveDimensions(this.baseWidth, this.baseHeight, this.state.rotation);
      const renderedWidth = eff.width * this.state.scale;
      const renderedHeight = eff.height * this.state.scale;
      this.state.x = (this.outputWidth - renderedWidth) / 2;
      this.state.y = (this.outputHeight - renderedHeight) / 2;
    }

    rotate(delta) {
      if (!this.ready || this.replacementPending || this.composition) return;
      const before = this.getEditorState();
      this.state.rotation = quarterTurns(this.state.rotation + Number(delta || 0));
      const fitScale = this.getFitScale();
      this.minScale = minScale(fitScale);
      if (this.state.scale < this.minScale) this.state.scale = this.minScale;
      this.setFillLimit(false);
      this.completeTransform(before, true);
      return this.getEditorState();
    }

    reset() {
      if (!this.ready || this.replacementPending || this.composition) return;
      const before = this.getEditorState();
      this.state = { x: 0, y: 0, scale: 1, rotation: 0 };
      this.state.scale = this.initialScale(0);
      this.centreState();
      this.setFillLimit(this.initialPlacement === 'fill' && this.getFillScale() > MAX_SCALE);
      this.completeTransform(before, true);
      return this.getEditorState();
    }

    setStateFromEditorState(editorState) {
      const restoration = this.validateRestoredState(editorState);
      if (!restoration) {
        const before = this.getEditorState();
        this.state.rotation = 0;
        this.state.scale = this.initialScale(0);
        this.centreState();
        this.setFillLimit(this.initialPlacement === 'fill' && this.getFillScale() > MAX_SCALE);
        this.completeTransform(before, true);
        return;
      }
      this.state.rotation = quarterTurns(restoration.rotation);
      this.state.scale = restoration.scale;
      this.state.x = restoration.x;
      this.state.y = restoration.y;
      if (Object.hasOwn(editorState, 'textLayers') || Object.hasOwn(editorState, 'textSchemaVersion')) {
        const layers = window.DIMTextModel?.textState ? window.DIMTextModel.textState(editorState) : editorState.textLayers;
        this.configureText(layers || [], { dirty: false, preserveHistory: false });
      }
      this.applyConstraints();
      this.updateControlsFromState();
      this.updateMeta();
      this.scheduleRender();
      this.updateGuidance();
    }

    setPosition(x, y, markDirty = true) {
      if (!this.ready || this.replacementPending || this.composition) return;
      const before = this.getEditorState();
      const eff = effectiveDimensions(this.baseWidth, this.baseHeight, this.state.rotation);
      const renderedWidth = eff.width * this.state.scale;
      const renderedHeight = eff.height * this.state.scale;
      this.state.x = clampPosition(toNumber(x), renderedWidth, this.outputWidth);
      this.state.y = clampPosition(toNumber(y), renderedHeight, this.outputHeight);
      this.completeTransform(before, markDirty);
    }

    zoomBy(delta) {
      if (!this.ready || this.replacementPending || this.composition) return;
      const before = this.getEditorState();
      const fitScale = this.getFitScale();
      const limit = minScale(fitScale);
      const next = clamp(this.state.scale + Number(delta), limit, MAX_SCALE);
      if (next === this.state.scale) return;
      this.state.scale = next;
      this.setFillLimit(false);
      this.completeTransform(before, true);
    }

    onSliderInput() {
      if (!this.ready || this.replacementPending || !this.sizeSlider || this.composition) return;
      const before = this.getEditorState();
      const raw = clamp(Number(this.sizeSlider.value), 1, MAX_SCALE * 100) / 100;
      const fitScale = this.getFitScale();
      const limit = minScale(fitScale);
      this.state.scale = clamp(raw, limit, MAX_SCALE);
      this.setFillLimit(false);
      this.completeTransform(before, true);
    }

    getFitScale(rotation = this.state.rotation) {
      if (this.composition) return 1;
      const eff = effectiveDimensions(this.baseWidth, this.baseHeight, rotation);
      return clamp(Math.min(this.outputWidth / eff.width, this.outputHeight / eff.height), 0.01, MAX_SCALE);
    }

    getFillScale() {
      if (this.composition) return 1;
      const eff = effectiveDimensions(this.baseWidth, this.baseHeight, this.state.rotation);
      return Math.max(this.outputWidth / eff.width, this.outputHeight / eff.height);
    }

    markDirty(immediate = false) {
      this.dirty = true;
      this.revision += 1;
      this.root.dataset.editorState = 'dirty';
      if (immediate) {
        if (this.dirtyEventRaf) window.cancelAnimationFrame(this.dirtyEventRaf);
        this.dirtyEventRaf = 0;
        this.emitDirty();
      } else this.queueDirtyEvent();
      this.updateControlsFromState();
      this.updateMeta();
      this.updateGuidance();
    }

    setClean() {
      this.dirty = false;
      this.root.dataset.editorState = 'ready';
      this.updateControlsFromState();
      this.updateMeta();
    }

    emitDirty() {
      emit(this.root, 'dreamit:editor-dirty', {
        dirty: this.dirty,
        state: this.getEditorState()
      });
    }

    queueDirtyEvent() {
      if (this.dirtyEventRaf) return;
      this.dirtyEventRaf = window.requestAnimationFrame(() => {
        this.dirtyEventRaf = 0;
        if (this.ready && this.dirty) this.emitDirty();
      });
    }

    sameTransform(first, second) {
      if (!first || !second) return false;
      return Math.abs(Number(first.x) - Number(second.x)) < SCALE_EPSILON
        && Math.abs(Number(first.y) - Number(second.y)) < SCALE_EPSILON
        && Math.abs(Number(first.scale) - Number(second.scale)) < SCALE_EPSILON
        && quarterTurns(first.rotation) === quarterTurns(second.rotation);
    }

    completeTransform(before, markAsDirty) {
      this.applyConstraints();
      const changed = !this.sameTransform(before, this.getEditorState());
      if (changed && markAsDirty) this.markDirty();
      else {
        this.updateControlsFromState();
        this.updateMeta();
        this.updateGuidance();
      }
      this.scheduleRender();
      return changed;
    }

    applyConstraints() {
      if (this.composition) { this.state = { x: 0, y: 0, scale: 1, rotation: 0 }; this.minScale = 1; return; }
      const fitScale = this.getFitScale();
      const minimum = minScale(fitScale);
      this.state.scale = clamp(this.state.scale, minimum, MAX_SCALE);
      const rotated = effectiveDimensions(this.baseWidth, this.baseHeight, this.state.rotation);
      const renderedWidth = rotated.width * this.state.scale;
      const renderedHeight = rotated.height * this.state.scale;
      this.state.x = clampPosition(this.state.x, renderedWidth, this.outputWidth);
      this.state.y = clampPosition(this.state.y, renderedHeight, this.outputHeight);
      this.minScale = minimum;
      if (this.sizeSlider) {
        this.sizeSlider.min = String(Math.round(this.minScale * 100));
        this.sizeSlider.max = String(MAX_SCALE * 100);
      }
    }

    updateControlsFromState() {
      if (this.sizeSlider) {
        this.sizeSlider.value = String(Math.round(clamp(this.state.scale, this.minScale, MAX_SCALE) * 100));
      }
      const atFit = Math.abs(this.state.scale - this.getFitScale()) < SCALE_EPSILON;
      const atFill = !atFit && this.getFillScale() <= MAX_SCALE
        && Math.abs(this.state.scale - this.getFillScale()) < SCALE_EPSILON;
      this.fitButton?.setAttribute('aria-pressed', String(atFit));
      this.fillButton?.setAttribute('aria-pressed', String(atFill));
      this.setCanvasLabel();
      this.updateMeta();
    }

    setCanvasLabel() {
      if (!this.canvas) return;
      if (this.composition) {
        this.canvas.setAttribute('aria-label', `${this.productName ? `${this.productName}: ` : ''}Words-only artwork, ${this.outputWidth}×${this.outputHeight} px`);
        return;
      }
      const ratioText = `${Math.round(this.state.scale * 100)}%`;
      const rotationText = `${quarterTurns(this.state.rotation) * 90}°`;
      const label = `${this.productName ? `${this.productName}: ` : ''}${this.baseWidth || 0}×${this.baseHeight || 0} source · ${ratioText} · ${rotationText}`;
      this.canvas.setAttribute('aria-label', label);
    }

    setFillLimit(visible) {
      const next = Boolean(visible);
      if (this.fillLimitVisible === next) return;
      this.fillLimitVisible = next;
      if (this.fillLimitGuidance) this.fillLimitGuidance.hidden = !next;
    }

    updateGuidance() {
      if (!this.ready) return;
      if (this.composition) {
        if (this.emptySpaceGuidance) this.emptySpaceGuidance.hidden = true;
        this.clearQualityState();
        return;
      }
      if (this.emptySpaceGuidance) this.emptySpaceGuidance.textContent = this.dynamicProductCopy(this.messages.emptySpace);
      if (this.emptySpaceGuidance && this.emptyGuidanceVisible !== true) {
        this.emptyGuidanceVisible = true;
        this.emptySpaceGuidance.hidden = false;
      }
      const quality = this.getQualityState();
      const percentage = String(quality.displayPercentage);
      const state = quality.prominentCaution ? 'caution' : 'neutral';
      if (this.root.dataset.editorQualityPercentage !== percentage) this.root.dataset.editorQualityPercentage = percentage;
      if (this.root.dataset.editorQualityState !== state) this.root.dataset.editorQualityState = state;
      if (this.qualityWarning) {
        if (this.qualityWarning.textContent !== this.messages.qualityWarning) this.qualityWarning.textContent = this.messages.qualityWarning;
        if (this.qualityCautionVisible !== quality.prominentCaution) this.qualityWarning.hidden = !quality.prominentCaution;
      }
      this.qualityCautionVisible = quality.prominentCaution;
    }

    getQualityState() {
      // `scale` maps decoded source pixels to resolved output pixels. This uses
      // the same rounded percentage as the size readout, never the CSS canvas.
      const sourceScale = Number(this.state.scale);
      const displayPercentage = Math.round(sourceScale * 100);
      const exactGovernedCreate = this.hasVerifiedCreateBaseline();
      return {
        sourceScale,
        displayPercentage,
        exactGovernedCreate,
        prominentCaution: displayPercentage >= 102 && !exactGovernedCreate
      };
    }

    clearQualityState() {
      if (this.root.dataset.editorQualityPercentage !== undefined) delete this.root.dataset.editorQualityPercentage;
      if (this.root.dataset.editorQualityState !== undefined) delete this.root.dataset.editorQualityState;
      if (this.qualityWarning) {
        if (this.qualityCautionVisible !== false) this.qualityWarning.hidden = true;
      }
      this.qualityCautionVisible = false;
    }

    hasVerifiedCreateBaseline() {
      const baseline = this.createBaseline;
      return Boolean(
        baseline
        && this.sourceRoute === 'create'
        && this.initialPlacement === baseline.initialPlacement
        && this.baseWidth === baseline.sourceSizePx?.width
        && this.baseHeight === baseline.sourceSizePx?.height
        && this.outputWidth === baseline.outputSizePx?.width
        && this.outputHeight === baseline.outputSizePx?.height
        && this.state.scale <= baseline.editorState.scale + SCALE_EPSILON
      );
    }

    updateMeta() {
      const copy = window.DreamItEditorControls;
      const dimensions = copy?.dimensionCopy?.({
        route: this.sourceRoute,
        sourceWidthPx: this.baseWidth,
        sourceHeightPx: this.baseHeight,
        outputWidthPx: this.outputWidth,
        outputHeightPx: this.outputHeight,
        outputWidthMm: this.outputPhysicalSizeMm?.width,
        outputHeightMm: this.outputPhysicalSizeMm?.height,
        ppi: this.ppi
      });
      if (this.originalDimensions) {
        this.originalDimensions.textContent = this.composition ? 'Words-only artwork' : dimensions?.source || `${this.sourceRoute === 'create' ? 'Generated' : 'Upload'} size: ${this.baseWidth} × ${this.baseHeight} px`;
      }
      if (this.outputDimensions) {
        this.outputDimensions.textContent = dimensions?.final || `Final size: ${this.outputWidth} × ${this.outputHeight} px`;
      }
      if (this.scaleReadout) {
        this.scaleReadout.textContent = this.composition ? '' : copy?.scaleCopy?.({ route: this.sourceRoute, scale: this.state.scale })
          || `Image size — ${Math.round(this.state.scale * 100)}% of source size`;
      }
      if (this.heading) this.heading.textContent = this.messages.heading;
      this.setCanvasLabel();
      this.updateGuidance();
    }

    dynamicProductCopy(template) {
      return window.DreamItEditorControls?.productCopy?.(template, this.productName)
        || String(template || '').replaceAll('[product]', this.productName || 'selected product');
    }

    enableControls(enabled) {
      [this.zoomOutButton, this.zoomInButton, this.sizeSlider, this.fitButton, this.fillButton, this.centreButton, this.rotateLeftButton, this.rotateRightButton, this.resetButton, this.exportButton, this.downloadButton, this.chooseButton]
        .forEach((element) => {
          if (!element) return;
          const isOutput = element === this.exportButton || element === this.downloadButton;
          const imageTransform = !isOutput && element !== this.chooseButton;
          const missingWords = this.composition && !this.textLayers.some(layer => layer.text.trim());
          const disabled = !enabled || this.replacementPending || (this.composition && imageTransform) || (isOutput
            && (this.root.dataset.forwardPending === 'true' || this.textPendingInvalid || missingWords));
          element.disabled = disabled;
          if (disabled) element.setAttribute('aria-disabled', 'true');
          else element.removeAttribute('aria-disabled');
        });
    }

    onPointerDown(event) {
      if (!this.ready || this.isInteractionLocked() || event.button !== 0) return;
      if (!this.bitmap && !this.composition) return;
      const task = this.directManipulationTask();
      if (task === 'locked' || task === 'layout' && this.composition) return;
      if (task !== 'legacy' && event.pointerType === 'touch' && event.isPrimary === false && !this.dragging) return;
      if (!Number.isFinite(event.clientX) || !Number.isFinite(event.clientY)) return;
      if (this.pointers.has(event.pointerId)) return;
      if (this.dragging) {
        // Only Layout's two touch pointers can share the image owner. Another
        // finger must never retarget Words or steal the legacy mouse owner.
        const first = this.pointers.get(this.activePointer);
        if (task !== 'layout' || this.gestureTask !== task || this.draggingKind !== 'image'
          || event.pointerType !== 'touch' || first?.type !== 'touch' || this.pointers.size !== 1) return;
        this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY, type: event.pointerType });
        this.canvas?.setPointerCapture(event.pointerId);
        this.rebasePinch();
        event.preventDefault();
        return;
      }
      if (!this.viewScaleX || !this.viewScaleY) this.layout();
      const rect = this.canvas?.getBoundingClientRect?.();
      const outputX = rect && this.viewScaleX ? (event.clientX - rect.left) / this.viewScaleX : NaN;
      const outputY = rect && this.viewScaleY ? (event.clientY - rect.top) / this.viewScaleY : NaN;
      const renderer = window.DIMTextRenderer;
      const hit = task !== 'layout' && Number.isFinite(outputX) && Number.isFinite(outputY) && renderer?.hit
        ? [...this.textLayouts].reverse().find((layout) => renderer.hit(
            layout.bounds, outputX, outputY, 8 / Math.max(this.viewScaleX, this.viewScaleY, 0.0001)
          ))
        : null;
      if (hit && this.textControls?.selectText?.(hit.layer.id)) this.scheduleRender();
      if (!hit && (this.composition || task === 'words')) return;
      if (!hit && task === 'legacy') this.textControls?.selectImage?.();
      this.draggingKind = hit ? 'text' : 'image';
      this.dragTextLayerId = hit?.layer.id || null;
      this.gestureTask = task;
      this.dragging = true;
      this.activePointer = event.pointerId;
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY, type: event.pointerType });
      this.lastPointer = { x: event.clientX, y: event.clientY };
      this.canvas?.setPointerCapture(event.pointerId);
      this.cancelPointerScroll = true;
      event.preventDefault();
    }

    onPointerMove(event) {
      if (!this.dragging || !this.pointers.has(event.pointerId)) return;
      if (!this.ready || this.isInteractionLocked() || this.gestureTask !== this.directManipulationTask()) {
        this.cancelPointer();
        return;
      }
      if (!Number.isFinite(event.clientX) || !Number.isFinite(event.clientY)) return;
      const point = this.pointers.get(event.pointerId);
      point.x = event.clientX; point.y = event.clientY;
      if (this.pointers.size === 2) {
        this.movePinch();
        return;
      }
      if (event.pointerId !== this.activePointer) return;
      const before = this.getEditorState();
      const dx = event.clientX - this.lastPointer.x;
      const dy = event.clientY - this.lastPointer.y;
      if (!this.viewScaleX || !this.viewScaleY) {
        this.layout();
      }
      const moveX = this.viewScaleX ? dx / this.viewScaleX : 0;
      const moveY = this.viewScaleY ? dy / this.viewScaleY : 0;
      if (this.draggingKind === 'text') {
        const layer = this.textLayers.find((item) => item.id === this.dragTextLayerId);
        if (layer) {
          this.textControls.applyPatch(layer.id, {
            x: clamp(layer.x + moveX / Math.max(1, this.outputWidth), -1, 2),
            y: clamp(layer.y + moveY / Math.max(1, this.outputHeight), -1, 2)
          }, { reason: 'text-drag', coalesceKey: `${layer.id}:canvas-drag` });
          this.lastPointer = { x: event.clientX, y: event.clientY };
          return;
        }
        // A removed/replaced text target cannot fall through to the image.
        this.cancelPointer();
        return;
      }
      this.state.x += moveX;
      this.state.y += moveY;
      this.lastPointer = { x: event.clientX, y: event.clientY };
      this.completeTransform(before, true);
    }

    onPointerEnd(event) {
      if (!this.pointers.has(event.pointerId)) return;
      this.pointers.delete(event.pointerId);
      // Delete tracking before release, which can synchronously signal lost capture.
      this.releasePointer(event.pointerId);
      if (!this.pointers.size) { this.cancelPointer(); return; }
      const [id, point] = this.pointers.entries().next().value;
      this.activePointer = id;
      this.lastPointer = { x: point.x, y: point.y };
      this.pinch = null;
    }

    pinchSample() {
      const [first, second] = this.pointers.values();
      if (!first || !second) return null;
      const rect = this.canvas.getBoundingClientRect();
      return {
        distance: Math.hypot(second.x - first.x, second.y - first.y),
        x: ((first.x + second.x) / 2 - rect.left) / this.viewScaleX,
        y: ((first.y + second.y) / 2 - rect.top) / this.viewScaleY
      };
    }

    rebasePinch() {
      this.pinch = { ...this.pinchSample(), state: { ...this.state } };
    }

    movePinch() {
      const sample = this.pinchSample();
      const baseline = this.pinch;
      if (!sample || !baseline) { this.rebasePinch(); return; }
      // A coincident start has no useful scale ratio. Wait for a stable pair
      // and use the last valid transform as its baseline without a jump.
      if (baseline.distance < 2 || sample.distance < 2) { this.rebasePinch(); return; }
      const before = this.getEditorState();
      const scale = clamp(baseline.state.scale * sample.distance / baseline.distance, minScale(this.getFitScale()), MAX_SCALE);
      const ratio = scale / baseline.state.scale;
      this.state.scale = scale;
      // x/y are the rotated image's bounding origin, so this also anchors
      // non-square artwork after the existing quarter-turn button rotation.
      this.state.x = sample.x - (baseline.x - baseline.state.x) * ratio;
      this.state.y = sample.y - (baseline.y - baseline.state.y) * ratio;
      if (scale !== before.scale) this.setFillLimit(false);
      this.completeTransform(before, true);
    }

    releasePointer(id) {
      try { this.canvas?.releasePointerCapture(id); } catch (_) { /* Capture may already have been lost. */ }
    }

    cancelPointer() {
      const captured = [...this.pointers.keys()];
      this.pointers.clear();
      this.dragging = false;
      this.draggingKind = 'image';
      this.dragTextLayerId = null;
      this.gestureTask = null;
      this.pinch = null;
      this.activePointer = null;
      this.lastPointer = { x: 0, y: 0 };
      this.cancelPointerScroll = false;
      captured.forEach(id => this.releasePointer(id));
    }

    scheduleRender() {
      if (this.rafId || !this.ready) return;
      this.rafId = window.requestAnimationFrame(() => {
        this.rafId = 0;
        this.render();
      });
    }

    layout() {
      if (!this.ready || !this.canvas) return { width: 0, height: 0, dpr: 1 };
      const rect = this.canvas.getBoundingClientRect();
      const width = Math.max(1, Math.round(rect.width));
      const height = Math.max(1, Math.round(rect.height));
      const dpr = Math.min(2, window.devicePixelRatio || 1);
      const backingWidth = Math.max(1, Math.round(width * dpr));
      const backingHeight = Math.max(1, Math.round(height * dpr));
      if (this.canvas.width !== backingWidth) this.canvas.width = backingWidth;
      if (this.canvas.height !== backingHeight) this.canvas.height = backingHeight;
      this.viewScaleX = width / this.outputWidth;
      this.viewScaleY = height / this.outputHeight;
      return { width, height, dpr };
    }

    initialScale(rotation = this.state.rotation) {
      if (this.initialPlacement !== 'fill') return this.getFitScale(rotation);
      const eff = effectiveDimensions(this.baseWidth, this.baseHeight, rotation);
      return clamp(Math.max(this.outputWidth / eff.width, this.outputHeight / eff.height), 0.01, MAX_SCALE);
    }

    updatePresentationSize() {
      if (!this.root?.style?.setProperty || !this.outputWidth || !this.outputHeight) return;
      const outputRatio = this.outputWidth / this.outputHeight;
      const overlayRatio = Number(this.root.dataset.imageOverlayFrameAspect);
      const ratio = Number.isFinite(overlayRatio) && overlayRatio > 0 ? overlayRatio : outputRatio;
      if (!Number.isFinite(ratio) || ratio <= 0) return;
      const compact = window.matchMedia?.('(max-width: 820px)')?.matches === true;
      const viewportHeight = Number(window.innerHeight) || 900;
      const maximumHeight = compact
        ? Math.max(220, Math.min(400, viewportHeight * 0.6))
        : Math.max(260, Math.min(600, viewportHeight * (viewportHeight <= 650 ? 0.5 : 0.62)));
      const maximumWidth = compact ? 400 : 640;
      const width = Math.min(maximumWidth, maximumHeight * ratio);
      this.root.style.setProperty('--dream-editor-display-width', `${Math.max(1, Math.round(width))}px`);
    }

    render() {
      if (!this.ready || !this.context || (!this.bitmap && !this.composition)) return;
      const { width, height, dpr } = this.layout();
      this.context.setTransform(1, 0, 0, 1, 0, 0);
      this.context.scale(dpr, dpr);
      if (this.composition) {
        this.context.fillStyle = this.composition.capability.background.colour;
        this.context.fillRect(0, 0, width, height);
      } else createCheckerboard(this.context, width, height, dpr);
      this.context.save();
      this.context.beginPath();
      this.context.rect(0, 0, width, height);
      this.context.clip();

      if (this.bitmap) {
        const effective = effectiveDimensions(this.baseWidth, this.baseHeight, this.state.rotation);
        const renderedWidth = effective.width * this.state.scale;
        const renderedHeight = effective.height * this.state.scale;
        const offsetX = this.state.x * this.viewScaleX;
        const offsetY = this.state.y * this.viewScaleY;
        const halfW = (renderedWidth * this.viewScaleX) / 2;
        const halfH = (renderedHeight * this.viewScaleY) / 2;
        const rotation = quarterTurns(this.state.rotation);
        const radians = rotation * (Math.PI / 2);

        this.context.translate(offsetX + halfW, offsetY + halfH);
        this.context.rotate(radians);
        this.context.scale(this.state.scale * this.viewScaleX, this.state.scale * this.viewScaleY);
        this.context.drawImage(this.bitmap, -this.baseWidth / 2, -this.baseHeight / 2, this.baseWidth, this.baseHeight);
      }
      this.context.restore();
      if (this.textLayouts.length) {
        this.context.save();
        try {
          this.context.scale(this.viewScaleX, this.viewScaleY);
          this.renderText(this.context, this.textLayouts, { display: true });
        } finally {
          this.context.restore();
        }
      }
      this.context.strokeStyle = 'rgba(0, 21, 39, 0.28)';
      this.context.lineWidth = 1.2;
      this.context.strokeRect(0.5, 0.5, width - 1, height - 1);
    }

    captureExportSnapshot() {
      return Object.freeze({
        revision: this.revision,
        productId: this.productId,
        outputWidth: this.outputWidth,
        outputHeight: this.outputHeight,
        baseWidth: this.baseWidth,
        baseHeight: this.baseHeight,
        sourceBlob: this.sourceBlob,
        composition: this.composition,
        bitmap: this.bitmap,
        editorState: this.getEditorState()
      });
    }

    async requestExport() {
      this.cancelPointer();
      if (this.exportInFlight || this.downloadInFlight || this.replacementPending || this.preparationOwners > 0) return null;
      if (!this.ready || (!this.bitmap && !this.composition) || !this.outputWidth || !this.outputHeight) {
        throw new Error('No image is ready to export.');
      }
      this.textControls?.assertPrintable?.();
      const snapshot = this.captureExportSnapshot();
      return this.performExport(snapshot);
    }

    async exportImage() {
      return this.requestExport();
    }

    async renderCurrentComposition(snapshot, staleMessage, diagnosticAttempt) {
      if (this.preparationOwners > 0) {
        throw new Error('Wait for the replacement image to finish, then prepare the output again.');
      }
      if (!this.exportSnapshotCurrent(snapshot)) {
        throw new Error(staleMessage);
      }
      exportDiagnostics.stage(diagnosticAttempt, 'admission');
      const placementConstraints = await this.verifyExportAdmission(snapshot);
      if (!this.exportSnapshotCurrent(snapshot)) throw new Error(staleMessage);
      exportDiagnostics.stage(diagnosticAttempt, 'text');
      const textLayouts = await this.ensureTextReady(snapshot, { admissionVerified: true, placementConstraints });
      if (!this.exportSnapshotCurrent(snapshot)) throw new Error(staleMessage);
      if (placementConstraints !== this.textPlacementConstraints) throw new Error(staleMessage);
      exportDiagnostics.stage(diagnosticAttempt, 'capacity');
      if (snapshot.composition) {
        window.DIMSceneIdentity.validateComposition(snapshot.composition);
        imageInspector.admitComposition({ outputWidth: snapshot.outputWidth, outputHeight: snapshot.outputHeight });
      } else imageInspector.admit({
        blob: this.sourceBlob,
        size: Number(this.sourceBlob?.size) || 0,
        width: this.baseWidth,
        height: this.baseHeight
      }, {
        outputWidth: snapshot.outputWidth,
        outputHeight: snapshot.outputHeight
      });
      exportDiagnostics.stage(diagnosticAttempt, 'allocate');
      const full = document.createElement('canvas');
      let compositionBlob;
      try {
        full.width = snapshot.outputWidth;
        full.height = snapshot.outputHeight;
        exportDiagnostics.stage(diagnosticAttempt, 'context');
        const fullContext = full.getContext('2d');
        if (!fullContext) throw Object.assign(new Error('Your browser could not prepare this full-quality image. Try another browser or device; the output has not been reduced.'), { code: 'IMAGE_OUTPUT_CAPACITY' });
        exportDiagnostics.stage(diagnosticAttempt, 'draw');
        fullContext.clearRect(0, 0, snapshot.outputWidth, snapshot.outputHeight);
        fullContext.imageSmoothingEnabled = true;
        fullContext.imageSmoothingQuality = 'high';
        if (snapshot.composition) {
          fullContext.fillStyle = snapshot.composition.capability.background.colour;
          fullContext.fillRect(0, 0, snapshot.outputWidth, snapshot.outputHeight);
        }
        if (snapshot.bitmap) {
          const state = snapshot.editorState;
          const effective = effectiveDimensions(this.baseWidth, this.baseHeight, state.rotation);
          const renderedWidth = effective.width * state.scale;
          const renderedHeight = effective.height * state.scale;
          fullContext.save();
          fullContext.translate(state.x + renderedWidth / 2, state.y + renderedHeight / 2);
          fullContext.rotate(state.rotation * (Math.PI / 2));
          fullContext.scale(state.scale, state.scale);
          fullContext.drawImage(this.bitmap, -this.baseWidth / 2, -this.baseHeight / 2, this.baseWidth, this.baseHeight);
          fullContext.restore();
        }
        this.renderText(fullContext, textLayouts, { display: false });
        if (!this.exportSnapshotCurrent(snapshot)) throw new Error(staleMessage);
        if (placementConstraints !== this.textPlacementConstraints) throw new Error(staleMessage);
        exportDiagnostics.stage(diagnosticAttempt, 'encode');
        compositionBlob = await new Promise((resolve) => {
          full.toBlob((blob) => resolve(blob), 'image/png');
        });
        if (!compositionBlob) exportDiagnostics.stage(diagnosticAttempt, 'null-blob');
        if (!compositionBlob) throw Object.assign(new Error('Your browser could not export this full-quality image. Try another browser or device; the output has not been reduced.'), { code: 'IMAGE_OUTPUT_CAPACITY' });
      } catch (error) {
        exportDiagnostics.finish(diagnosticAttempt, error);
        if (error instanceof RangeError || ['QuotaExceededError', 'IndexSizeError'].includes(error?.name)) {
          throw Object.assign(new Error('Your browser could not prepare this full-quality image. Try another browser or device; the output has not been reduced.'), { code: 'IMAGE_OUTPUT_CAPACITY' });
        }
        throw error;
      } finally {
        full.width = 0;
        full.height = 0;
      }
      exportDiagnostics.stage(diagnosticAttempt, 'verify-png');
      const verified = await imageInspector.inspect(compositionBlob, { expectedMime: 'image/png' });
      if (verified.width !== snapshot.outputWidth || verified.height !== snapshot.outputHeight) {
        throw new Error('The exported image dimensions did not match the selected product specification.');
      }
      exportDiagnostics.stage(diagnosticAttempt, 'verify-capacity');
      imageInspector.admitEncodedOutput(verified, {
        sourceWidth: this.baseWidth,
        sourceHeight: this.baseHeight,
        sourceEncodedBytes: Number(this.sourceBlob?.size) || 0,
        outputWidth: snapshot.outputWidth,
        outputHeight: snapshot.outputHeight,
        includeDecodedOutput: !verified.semantic,
        ...(snapshot.composition ? { composition: snapshot.composition } : {})
      });
      if (!verified.semantic) {
        exportDiagnostics.stage(diagnosticAttempt, 'decode');
        const decoded = await this.decodeExportedBlob(compositionBlob);
        if (decoded.width !== snapshot.outputWidth || decoded.height !== snapshot.outputHeight) {
          throw new Error('The exported image dimensions did not match the selected product specification.');
        }
      }
      if (!this.exportSnapshotCurrent(snapshot)) {
        throw new Error(staleMessage);
      }
      if (placementConstraints !== this.textPlacementConstraints) throw new Error(staleMessage);
      return compositionBlob;
    }

    async requestDownload() {
      this.cancelPointer();
      if (this.downloadInFlight || this.exportInFlight || this.replacementPending || this.preparationOwners > 0 || this.root.dataset.forwardPending === 'true') return null;
      if (!this.ready || (!this.bitmap && !this.composition) || !this.outputWidth || !this.outputHeight) {
        throw new Error('No image is ready to download.');
      }
      this.textControls?.assertPrintable?.();
      const snapshot = this.captureExportSnapshot();
      const diagnosticAttempt = exportDiagnostics.begin(this, snapshot);
      const wasDirty = this.dirty;
      const stateBeforeDownload = this.getEditorState();
      this.downloadInFlight = true;
      this.enableControls(false);
      this.downloadButton?.setAttribute('aria-busy', 'true');
      try {
        const compositionBlob = await this.renderCurrentComposition(
          snapshot,
          'The image changed while it was being downloaded. Select Download image again.', diagnosticAttempt
        );
        this.releaseDownloadUrl();
        const objectUrl = URL.createObjectURL(compositionBlob);
        this.downloadUrl = objectUrl;
        const link = document.createElement('a');
        link.href = objectUrl;
        link.download = 'dream-it-made-image.png';
        link.hidden = true;
        document.body.appendChild(link);
        link.click();
        link.remove();
        this.downloadUrlTimer = window.setTimeout(() => {
          if (this.downloadUrl === objectUrl) this.releaseDownloadUrl();
        }, 60000);
        if (this.dirty !== wasDirty || JSON.stringify(this.getEditorState()) !== JSON.stringify(stateBeforeDownload)) {
          throw new Error('The editor state changed while the image was being downloaded.');
        }
        emit(this.root, 'dreamit:editor-downloaded', {
          width: snapshot.outputWidth,
          height: snapshot.outputHeight,
          revision: snapshot.revision
        });
        exportDiagnostics.stage(diagnosticAttempt, 'complete');
        exportDiagnostics.finish(diagnosticAttempt, null, compositionBlob.size);
        return compositionBlob;
      } catch (error) {
        exportDiagnostics.finish(diagnosticAttempt, error);
        throw error;
      } finally {
        this.downloadInFlight = false;
        this.downloadButton?.removeAttribute('aria-busy');
        this.enableControls(this.ready && !this.destroyed);
      }
    }

    async performExport(snapshot) {
      if (this.exportInFlight || this.downloadInFlight || this.replacementPending || this.preparationOwners > 0) return null;
      if (!this.ready || (!this.bitmap && !this.composition) || !this.outputWidth || !this.outputHeight) {
        throw new Error('No image is ready to export.');
      }
      const diagnosticAttempt = exportDiagnostics.begin(this, snapshot);
      this.exportInFlight = true;
      this.root.dataset.editorExporting = 'true';
      this.enableControls(false);
      if (this.exportButton) this.exportButton.setAttribute('aria-busy', 'true');
      emit(this.root, 'dreamit:editor-export-start', { revision: snapshot.revision });
      try {
        const state = snapshot.editorState;
        const exportedBlob = await this.renderCurrentComposition(
          snapshot,
          'The image changed while it was being prepared. Select Continue again.', diagnosticAttempt
        );
        this.setClean();
        exportDiagnostics.stage(diagnosticAttempt, 'rendered');
        exportDiagnostics.finish(diagnosticAttempt, null, exportedBlob.size);
        emit(this.root, 'dreamit:editor-exported', {
          diagnosticAttempt,
          blob: exportedBlob,
          width: snapshot.outputWidth,
          height: snapshot.outputHeight,
          editorState: snapshot.editorState,
          ...(snapshot.composition ? { composition: snapshot.composition } : {}),
          enlargementConfirmed: state.scale > 1,
          revision: snapshot.revision
        });
        return exportedBlob;
      } catch (error) {
        exportDiagnostics.finish(diagnosticAttempt, error);
        throw error;
      } finally {
        this.exportInFlight = false;
        delete this.root.dataset.editorExporting;
        if (this.exportButton && this.root.dataset.forwardPending !== 'true') this.exportButton.removeAttribute('aria-busy');
        this.enableControls(this.ready && !this.destroyed);
      }
    }

    async decodeExportedBlob(blob) {
      if (window.createImageBitmap) {
        let bitmap;
        try {
          bitmap = await createImageBitmap(blob);
          return { width: bitmap.width, height: bitmap.height };
        } finally {
          if (bitmap?.close) bitmap.close();
        }
      }
      const objectUrl = URL.createObjectURL(blob);
      const image = new Image();
      try {
        return await new Promise((resolve, reject) => {
          image.onload = () => resolve({ width: image.naturalWidth, height: image.naturalHeight });
          image.onerror = () => reject(new Error('Failed to verify the prepared image.'));
          image.src = objectUrl;
        });
      } finally {
        image.onload = null;
        image.onerror = null;
        image.src = '';
        URL.revokeObjectURL(objectUrl);
      }
    }

    applyState() {
      this.applyConstraints();
      this.updateControlsFromState();
      this.scheduleRender();
    }

    releaseDownloadUrl() {
      if (this.downloadUrlTimer) window.clearTimeout(this.downloadUrlTimer);
      this.downloadUrlTimer = 0;
      if (this.downloadUrl) URL.revokeObjectURL(this.downloadUrl);
      this.downloadUrl = null;
    }

    destroy() {
      this.releaseDownloadUrl();
      window.removeEventListener('pagehide', this.onDownloadPageExit);
      this.unload();
      this.textControls?.destroy?.();
      this.textControls = null;
      if (this.bitmap) {
        releaseDecodedImage(this.bitmap);
      }
      this.bitmap = null;
      this.sourceBlob = null;
      this.composition = undefined;
      this.createBaseline = null;
      this.root?.style?.removeProperty?.('--dream-editor-display-width');
      this.ready = false;
      this.destroyed = true;
      window.removeEventListener('resize', this.onWindowResize);
      this.root.removeEventListener?.('dreamit:editor-presentation-frame-change', this.onPresentationFrameChange);
      this.root.removeEventListener?.('dreamit:editor-task-change', this.onInteractionOwnerChange);
      this.root.removeEventListener?.('dreamit:editor-surface-change', this.onInteractionOwnerChange);
      this.root.removeEventListener?.('dreamit:editor-nudge', this.onNudgeControl);
      if (this.resizeObserver) {
        this.resizeObserver.disconnect();
        this.resizeObserver = null;
      }
      if (this.rafId) {
        window.cancelAnimationFrame(this.rafId);
        this.rafId = 0;
      }
      if (this.dirtyEventRaf) {
        window.cancelAnimationFrame(this.dirtyEventRaf);
        this.dirtyEventRaf = 0;
      }
      if (this.canvas) {
        const context = this.canvas.getContext('2d');
        if (context) context.clearRect(0, 0, this.canvas.width, this.canvas.height);
      }
      this.enableControls(false);
      if (this.heading) this.heading.textContent = this.messages.heading;
      this.alert?.setAttribute('hidden', 'true');
    }

    unload() {
      this.cancelPointer();
      this.releaseDownloadUrl();
      if (this.preparedCandidate) this.discardPreparedLoad(this.preparedCandidate);
      this.setReplacementPending(false);
      this.revision += 1;
      if (this.bitmap) {
        releaseDecodedImage(this.bitmap);
      }
      this.bitmap = null;
      this.sourceBlob = null;
      this.composition = undefined;
      this.createBaseline = null;
      this.ready = false;
      this.dirty = false;
      this.clearQualityState();
      this.emptyGuidanceVisible = null;
      this.setFillLimit(this.initialPlacement === 'fill' && this.getFillScale() > MAX_SCALE);
      this.root?.style?.removeProperty?.('--dream-editor-display-width');
      this.state = { x: 0, y: 0, scale: 1, rotation: 0 };
      this.textLayers = Object.freeze([]);
      this.textLayouts = Object.freeze([]);
      this.textPendingInvalid = false;
      this.textLayoutError = null;
      this.textLayoutRequest += 1;
      this.textControls?.setInkColours?.(null);
      this.textControls?.setLayers?.([], { dirty: false, preserveHistory: false });
      this.updateTextControlsVisibility();
      this.setTextPlacementConstraints(null);
    }
  }

  window.DreamItImageEditor = DreamItImageEditor;
})();
